import { action, computed, observable } from 'mobx';
import stores from '../../../models/Stores';
import sleep from 'cd-eft/utils/sleep';
import DelineateWatershedGpApi, {
  IDelineateWatershedRequestParams,
  IDelineateWatershedResponse,
  ILoadSessionResponse,
  IStreamlineResponse,
} from 'cd-eft/services/DelineateWatershedGpApi/DelineateWatershedGpApi';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import { ProjectCommentRequest } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin';
import { ActivityLogController, HydroWatershedController } from 'cd-eft/services/eftSetApi';
import '../DelineateWatershedInput/delineateWatershedInput.scss';

import {
  MapView,
  GraphicsLayer,
  Sketch,
  GroupLayer,
  RasterData,
  FeatureSet,
  esriProjection,
  Symbol,
  Geometry,
  esriGeometryEngine,
  Graphic,
  SpatialReference,
  Query,
  ImageryLayer,
  SketchViewModel,
} from 'cd-eft/utils/esriTypes';
import Search from '@arcgis/core/widgets/Search';
import Point from '@arcgis/core/geometry/Point';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

import Layer from '@arcgis/core/layers/Layer';
import * as gisUtils from 'cd-eft/utils/gisUtils';
import * as mapPreferences from 'cd-eft/utils/mapPreferences';
import GraphicsLayerView from '@arcgis/core/views/layers/GraphicsLayerView';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import esriRequest from '@arcgis/core/request';
import esriConfig from '@arcgis/core/config';

import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Viewpoint from '@arcgis/core/Viewpoint';
import Extent from '@arcgis/core/geometry/Extent';
import { UserIdentity } from 'cd-eft/security/UserIdentity';
import { EftProjectProjectionTextDto, StateCountyDto } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin/api';
import { appConfig } from 'cd-eft/config/AppConfig';
import { projeectionTextObj } from 'cd-eft/models/Projects/projectsLibrary';
import Color from '@arcgis/core/Color';

import { EditSoilAndLandVM } from '../EditSoilAndLand/EditSoilAndLandVM';
import { VelocityMethodVM } from './VelocityMethod/VelocityMethodVM';
import { validateDemFile } from 'cd-eft/utils/fileUploadUtils';

// DEM export parameters
const demClipMaxSize: number = 3200000; // 4000000; // no more than 4 million pixels (3.2 million now)
const demServiceTimeout: number = 120000; // 120 seconds for now
const demIdentifyTimeout: number = 20000; // 20 seconds for now
const __minScreenshotSize = 500;

export const workflowStepNames: Readonly<string[]> = [
  'Define AOI and DEM',
  'Define Outlet',
  'Edit Watershed',
  'Edit Land Use',
  'Select HSG',
  'Results',
];

const aoiGraphicSymbol = {
  type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
  color: [0, 0, 0, 0],
  style: 'none',
  outline: {
    // autocasts as new esri.SimpleLineSymbol()
    color: 'red',
    width: 2,
  },
} as any;

const culvertGraphicSymbol = {
  type: 'simple-line', // autocasts as new esri.SimpleLineSymbol()
  color: 'blue',
  width: 2,
} as any;

const outletGraphicSymbol = {
  type: 'simple-line', // autocasts as new SimpleLineSymbol()
  color: 'green',
  width: 2,
} as any;

const invalidWatershedSymbol = {
  type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
  color: [255, 0, 0, 0.3],
  style: 'diagonal-cross',
  outline: {
    // autocasts as new esri.SimpleLineSymbol()
    color: [255, 0, 0, 0.3],
    width: 4,
  },
} as any;

const validWatershedSymbol = {
  type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
  color: [0, 127, 200, 0.3],
  style: 'solid',
  outline: {
    // autocasts as new esri.SimpleLineSymbol()
    color: [255, 255, 255, 0.6],
    width: 2,
  },
} as any;

const badTopologyWatershedSymbol = {
  type: 'simple-fill', // autocasts as new esri.SimpleFillSymbol()
  color: [0, 127, 200, 0.3],
  style: 'diagonal-cross',
  outline: {
    // autocasts as new esri.SimpleLineSymbol()
    color: [0, 127, 200, 0.3],
    width: 4,
  },
} as any;

const invalidFlowPathSymbol = {
  type: 'simple-line', // autocasts as new SimpleLineSymbol()
  color: [255, 0, 0, 0.3],
  width: 4,
} as any;

const validFlowPathSymbol = {
  type: 'simple-line', // autocasts as new SimpleLineSymbol()
  color: 'blue',
  width: 2,
} as any;

const intersectionSymbol = {
  type: 'simple-marker',
  style: 'x',
  color: 'red',
  size: '12px',
  outline: {  // autocasts as new SimpleLineSymbol()
    color: 'red',
    width: 5  // points
  }
} as any;

// const __editSoilSymbol = new SimpleFillSymbol({
//   style: 'none',
//   color: [0, 0, 0, 0],
//   outline: {
//     type: 'simple-line',
//     style: 'dot',
//     color: 'black',
//     width: '2px',
//   },
// });

const __editLandUseSymbol = new SimpleFillSymbol({
  style: 'none',
  color: [0, 0, 0, 0],
  outline: {
    type: 'simple-line',
    style: 'dot',
    color: 'maroon', // 'yellow',
    width: '2px',
  },
});

//NOTE: visual draw order
const ___resultLayerNames = [
  'result_dem_layer',
  'result_dem_boundary_layer',
  'result_clipdem_layer',
  'result_depthgrid_layer',
  'result_hillshade_layer',
  'result_contours_layer',
  'result_streamlines_layer',
  'result_aoi_layer',
  'result_culverts_layer',
  'result_outlets_layer',
  'result_watershed_layer',
  'result_flowpath_layer',
  'result_soils_layer',
  // 'output_SAL_LANDUSE_layer',
  'Land Use',
  'output_SAL_WATERSHED_layer',
  'output_RCN_SUBAREAS_layer',
  'output_RCN_WATERSHED_layer',
];

//NOTE: visual draw order
const ___resultNames = [
  'result_dem',
  'result_dem_boundary',
  'result_clipdem',
  'result_depthgrid',
  'result_hillshade',
  'result_contours',
  'result_streamlines',
  'result_aoi',
  'result_culverts',
  'result_outlets',
  'result_watershed',
  'result_flowpath_orig',
  'result_flowpath',
  'result_soils',
  // 'output_SAL_LANDUSE',
  'salLandGraphics',
  // 'output_SAL_WATERSHED',
  'result_rcn_subareas',
  'result_rcn_watershed',
];

const ___resultNamesByStep = [
  { result: 'result_dem', step: 0 },
  { result: 'result_dem_boundary', step: 0 },
  { result: 'result_clipdem', step: 1 },
  { result: 'result_depthgrid', step: 1 },
  { result: 'result_hillshade', step: 1 },
  { result: 'result_contours', step: 1 },
  { result: 'result_streamlines', step: 1 },
  { result: 'result_aoi', step: 1 },
  { result: 'result_culverts', step: 2 },
  { result: 'result_watershed', step: 2 },
  { result: 'result_flowpath_orig', step: 2 },
  { result: 'result_flowpath', step: 2 },
  { result: 'result_soils', step: 3 },
  { result: 'salLandGraphics', step: 3 },
  { result: 'result_rcn_subareas', step: 4 },
  { result: 'result_rcn_watershed', step: 4 },
  { result: 'result_outlets', step: 2 }
];

const salSoilSymbol = {
  type: 'simple-fill',
  style: 'none',
  color: [0, 0, 0, 0],
  outline: {
    type: 'simple-line',
    style: 'dot',
    color: 'black',
    width: '2px',
  }
} as any;

const rcnWatershedSymbol = {
  type: 'simple-fill', // autocasts as new SimpleFillSymbol()
  color: [0, 0, 0, 0],
  style: 'none',
  outline: {
    // autocasts as new SimpleLineSymbol()
    color: 'red',
    width: 2,
  },
} as any;

const efh2Store = stores.AppStore.efh2Store;
const __lulcInputTypeKey: string = 'etc...'; // 'hydrology-lulc-data';
const __notAvailableSuffix: string = ' NOT AVAILABLE';

const _newHighlightOptions = {
  color: new Color('rgb(0, 255, 255)'),
  fillOpacity: 0
};

const _selectSALSymbol = {
  type: 'simple-fill', // autocasts as new SimpleFillSymbol()
  color: [0, 255, 255, 0.25],
  style: 'solid',
  outline: {
    // autocasts as new SimpleLineSymbol()
    color: 'cyan',
    width: 2,
  },
} as any;

type beforeActivityUpdateCallback = (
  eventData: { eventName: 'submit' | 'undo' | 'redo' },
  eventSource: DelineateWatershedInputStateVM
) => void;

type onActivityUpdateCallback = (
  eventData: { eventName: 'submit' | 'undo' | 'redo' },
  eventSource: DelineateWatershedInputStateVM
) => Promise<void>;

type GpLinearUnit = 'feet' | 'meters';
export class DelineateWatershedInputStateVM {
  @observable private __currentStepIndex: number = 0;
  // TODO: not sure these need to be observable, maybe can be private, readonly and/or @calculated?
  @observable jobExecuting: boolean = false;
  @observable jobMessages: string[] = [];
  @observable gpSessionId: string = '';

  @observable countyExtent?: Geometry;

  @observable expressMode: boolean = false;
  @observable in_dem_service_selection: 'service' | 'upload' | undefined = undefined;
  @observable in_dem_file: File | undefined = undefined;
  @observable in_dem_elevation_units: string = '';
  @observable in_generate_contours: boolean = false;
  @observable in_contour_interval: number = 2.0;
  @observable in_generate_hillshade: boolean = true;
  @observable in_generate_depthgrid: boolean = false;
  @observable in_generate_streamlines: boolean = true;
  @observable in_stream_threshold: number = 2.0;
  @observable previouslyHadCulverts = false;
  @observable projectionText!: string;
  private toggleHillshadeButton = false;

  @observable in_dem_id: string = '';
  @observable in_dem_title: string = '';
  demSelectName: string = '';
  private in_dem_service_url: string = '';
  private in_dem_metadata_url: string = '';
  private errorDemServices: string[] = [];
  @observable in_dem_hillshade: string = '';
  @observable demMetadataValues: any = [];
  @observable isDemMetadataWindowVisible: boolean = false;

  @observable result_dem: RasterData | undefined;
  @observable result_clipdem: RasterData | undefined;
  @observable result_hillshade: RasterData | undefined;
  @observable result_depthgrid: RasterData | undefined;

  @observable result_dem_boundary: FeatureSet | undefined;

  @observable result_aoi: FeatureSet | undefined;
  @observable result_culverts: FeatureSet | undefined;
  @observable result_outlets: FeatureSet | undefined;
  @observable result_contours: FeatureSet | undefined;
  @observable result_streamlines: FeatureSet | undefined;
  @observable result_watershed: FeatureSet | undefined;
  @observable result_upd_watershed: FeatureSet | undefined;
  @observable result_flowpath: FeatureSet | undefined;

  @observable output_SAL_WATERSHED?: FeatureSet;
  @observable result_soils?: FeatureSet;
  @observable output_SAL_LANDUSE?: FeatureSet;
  @observable output_RCN_WATERSHED?: FeatureSet;
  @observable output_RCN_SUBAREAS?: FeatureSet;

  private edit_SAL_LANDUSE_graphicsLayer?: GraphicsLayer;
  @observable result_soils_layer?: FeatureLayer;
  @observable public edit_SAL_LANDUSE_graphicsLayerView?: GraphicsLayerView;
  @observable public salSoilGraphicsLayerView?: GraphicsLayerView;
  @observable public readonly selectedLandUse: any[] = observable.array([], { deep: false }); // { deep: false } ensures that only the array structure is observable, contents are left untouched by mobx
  @observable public selectedSoils: number[] = [];
  public editSoilVm?: EditSoilAndLandVM;
  public velocityVm?: VelocityMethodVM;

  // @observable salLandArray: any[] = [];
  @observable salSoilArray: any[] = [];
  @observable rcnWatershedArray: any[] = [];
  @observable rcnSubareasArray: any[] = [];
  @observable landUseTableComplete: boolean = false;
  @observable hasSoilError: boolean = false;
  @observable hydrologyValidationError: boolean = false;
  @observable aoiArea: string | undefined;
  watershedAcreage: number = 0; // MIGHT CHANGE LATER
  @observable flowPathLength: string | undefined;
  getFlowpathLength(): string | undefined {
    return this.flowPathLength;
  }

  private demFileCellSize?: number = undefined;
  private currentDemCellSize?: number = undefined;
  public workingSpatialReference?: SpatialReference = undefined;
  public watershedModified: boolean = false;
  private flowPathModified: boolean = false;
  private demModified: boolean = false;
  private aoiModified: boolean = false;
  private suppressDemServiceLoading = false;

  @observable public isDemServiceAvailable: boolean = false;
  @observable public availableDemServices: IAvailableDemService[] = [];
  private configDemServices: any[] = [];

  public readonly beforeActivityUpdateCallback: beforeActivityUpdateCallback;
  public readonly onActivityUpdateCallback: onActivityUpdateCallback;
  public readonly aoiSketch: Sketch;
  public readonly culvertSketch: Sketch;
  public readonly outletSketch: Sketch;
  public readonly watershedSketch: Sketch;
  public readonly flowPathSketch: SketchViewModel;
  // public readonly flowPathSketch: Sketch;
  public readonly searchBar: Search;

  private readonly api: DelineateWatershedGpApi;
  public readonly mapView: MapView;
  public readonly aoiGraphicsLayer: GraphicsLayer;
  public readonly culvertGraphicsLayer: GraphicsLayer;
  public readonly outletGraphicsLayer: GraphicsLayer;
  public readonly watershedGraphicsLayer: GraphicsLayer;
  public readonly flowPathGraphicsLayer: GraphicsLayer;
  public readonly separatorGraphicsLayer: GraphicsLayer;
  public readonly flowpathSegmentsGraphicsLayer: GraphicsLayer;
  private readonly intersectGraphicsLayer: GraphicsLayer;
  public readonly salSoilGraphicsLayer: GraphicsLayer;
  private readonly rcnWatershedGraphicsLayer: GraphicsLayer;
  public readonly salSelectGraphicsLayer: GraphicsLayer;
  @observable salSelectionSetup = false;
  private oldHighlightOptions: any;
  @observable highlightOid = -1;

  public readonly sketchGroupLayer: GroupLayer;
  public readonly drawingsGroupLayer: GroupLayer;
  public readonly measuresGroupLayer: GroupLayer;
  public readonly resultsGroupLayer: GroupLayer;
  public readonly demServicesGroupLayer: GroupLayer;
  private readonly demServicesProperties: any[] = [];
  private bestAvailableHillshade: ImageryLayer | undefined;

  public originalWatershedGraphic: Graphic | undefined;
  @observable outletsFeature: FeatureSet | undefined;
  @observable public isEditWatershedOn: boolean = true;
  @observable public isEditFlowPathOn: boolean = true;
  @observable public createPolylineDisabled: boolean = false;
  @observable public canUndoFlowPath: boolean = false;
  @observable public canRedoFlowPath: boolean = false;
  @observable public canResetSmartCut: boolean = false;
  private mapContainer: HTMLElement;

  private watchHandle1;
  private watchHandle2;
  private watchHandle3;
  private watchHandle4;
  private watchHandle5;
  private watchHandle6;
  private watchHandle7;
  private watchHandle8;
  private watchHandle9;
  private watchHandle10;
  private watchHandle11;
  private watchHandle12;
  private watchHandle13;
  private watchHandle14;
  private watchHandle15;
  private watchHandle16;
  private watchHandle17;
  private watchHandle18;
  private watchHandle19;
  private watchHandle20;
  private watchHandle21;
  private watchHandle22;
  private watchHandle23;

  dispose(): void {
    if (this.mapView) this.mapView.highlightOptions = this.oldHighlightOptions;
    this.watchHandle1.remove();
    this.watchHandle2.remove();
    this.watchHandle3.remove();
    this.watchHandle4.remove();
    this.watchHandle5.remove();
    this.watchHandle6.remove();
    this.watchHandle7.remove();
    this.watchHandle8.remove();
    this.watchHandle9.remove();
    this.watchHandle10.remove();
    this.watchHandle11?.remove();
    this.watchHandle12?.remove();
    this.watchHandle13?.remove();
    this.watchHandle14?.remove();
    this.watchHandle15?.remove();
    this.watchHandle16?.remove();
    this.watchHandle17?.remove();
    this.watchHandle18?.remove();
    this.watchHandle19.remove();
    this.watchHandle20.remove();
    this.watchHandle21.remove();
    this.watchHandle22.remove();
    this.watchHandle23.remove();
    this.unmountLayers();
    this.aoiGraphicsLayer.destroy();
    this.culvertGraphicsLayer.destroy();
    this.outletGraphicsLayer.destroy();
    this.watershedGraphicsLayer.destroy();
    this.flowPathGraphicsLayer.destroy();
    this.separatorGraphicsLayer.destroy();
    this.flowpathSegmentsGraphicsLayer.destroy();
    this.intersectGraphicsLayer.destroy();
    this.sketchGroupLayer.destroy();
    this.drawingsGroupLayer.destroy();
    this.measuresGroupLayer.destroy();
    this.resultsGroupLayer.destroy();
    this.aoiSketch.destroy();
    this.culvertSketch.destroy();
    this.outletSketch.destroy();
    this.watershedSketch.destroy();
    this.flowPathSketch.destroy();
    this.searchBar.destroy();
  }

  constructor(
    api: DelineateWatershedGpApi,
    mapView: MapView,
    drawingsGroupLayer: GroupLayer,
    measuresGroupLayer: GroupLayer,
    onActivityUpdateCallback?: onActivityUpdateCallback,
    gpSessionData?: ILoadSessionResponse,
    gpSessionId?: string,
    beforeActivityUpdateCallback?: beforeActivityUpdateCallback,
    loadedInput?: { soilArray; landArray },
    lulcData?
  ) {
    if (!api) {
      throw new Error('Null arguments: api');
    }
    if (!mapView) {
      throw new Error('Null arguments: mapView');
    }

    this.api = api;
    this.mapView = mapView;
    this.oldHighlightOptions = this.mapView.highlightOptions;
    this.mapView.highlightOptions = _newHighlightOptions;

    this.countyExtent = undefined;

    this.aoiGraphicsLayer = new GraphicsLayer({
      id: 'aoiGraphicsLayer',
      title: 'AOIGraphics',
      visible: true,
    });

    this.culvertGraphicsLayer = new GraphicsLayer({
      id: 'culvertGraphicsLayer',
      title: 'CulvertsGraphics',
      visible: true,
    });

    this.outletGraphicsLayer = new GraphicsLayer({
      id: 'outletGraphicsLayer',
      title: 'OutletGraphics',
      visible: true,
    });

    this.watershedGraphicsLayer = new GraphicsLayer({
      id: 'watershedGraphicsLayer',
      title: 'WatershedGraphics',
      visible: true,
    });

    this.flowPathGraphicsLayer = new GraphicsLayer({
      id: 'flowPathGraphicsLayer',
      title: 'FlowPathGraphics',
      visible: true,
    });

    this.separatorGraphicsLayer = new GraphicsLayer({
      id: 'separatorGraphicsLayer',
      title: 'SeparatorGraphics',
      visible: true,
    });

    this.flowpathSegmentsGraphicsLayer = new GraphicsLayer({
      id: 'flowpathSegmentsGraphicsLayer',
      title: 'FlowpathSegmentsGraphics',
      visible: true,
    });

    this.intersectGraphicsLayer = new GraphicsLayer({
      id: 'intersectGraphicsLayer',
      title: 'Watershed Self-Intersection Graphics',
      visible: true,
    });

    this.salSoilGraphicsLayer = new GraphicsLayer({
      id: 'soilGraphicsLayer',
      title: 'SoilGraphics',
      visible: false,
    });
    this.salSoilGraphicsLayer.on('layerview-create', (e) => {
      this.salSoilGraphicsLayerView = e.layerView as GraphicsLayerView;
    });
    this.salSoilGraphicsLayer.on('layerview-destroy', (e) => {
      this.salSoilGraphicsLayerView = undefined;
    });

    this.rcnWatershedGraphicsLayer = new GraphicsLayer({
      id: 'rcnGraphicsLayer',
      title: 'RcnGraphics',
      visible: false,
    });

    this.salSelectGraphicsLayer = new GraphicsLayer({
      id: 'salSelect',
      title: 'LULC and Soil Selection',
      visible: false,
      listMode: 'hide'
    });

    this.sketchGroupLayer = new GroupLayer({
      id: 'sketchGroupLayer',
      title: 'Watershed Analysis - Input',
      listMode: 'hide',
      layers: [
        this.aoiGraphicsLayer,
        this.culvertGraphicsLayer,
        this.outletGraphicsLayer,
        this.intersectGraphicsLayer,
        this.watershedGraphicsLayer,
        this.flowPathGraphicsLayer,
        this.flowpathSegmentsGraphicsLayer,
        this.separatorGraphicsLayer,
        this.salSoilGraphicsLayer,
        this.rcnWatershedGraphicsLayer,
      ],
    });

    this.drawingsGroupLayer = drawingsGroupLayer;
    this.measuresGroupLayer = measuresGroupLayer;

    this.resultsGroupLayer = new GroupLayer({
      id: 'resultsGroupLayer',
      title: 'Watershed Analysis',
      layers: [],
    });

    this.demServicesGroupLayer = new GroupLayer({
      id: 'demServicesGroupLayer',
      title: 'DEM Services',
      layers: [],
    });


    this.mountLayers();

    this.aoiSketch = new Sketch({
      id: 'aoiSketch',
      layer: this.aoiGraphicsLayer,
      view: this.mapView,
      creationMode: 'single',
      visibleElements: {
        createTools: {
          point: false,
          polyline: false,
          polygon: true,
          rectangle: true,
          circle: true,
        },
        selectionTools: {
          'rectangle-selection': true,
          'lasso-selection': true,
        },
        settingsMenu: false,
        undoRedoMenu: false,
      } as any,
    });
    this.aoiSketch.viewModel.polygonSymbol = aoiGraphicSymbol;
    this.watchHandle10 = this.aoiSketch.on('update', this.aoiSketchUpdateHandler.bind(this));

    this.watchHandle11 = this.aoiSketch.on('create', async (event) => this.watchHandle11CreateCallback(event));

    this.watchHandle12 = this.aoiSketch.on('delete', () => {
      this.aoiArea = undefined;
      this.disableServiceDems();

      if (this.in_dem_service_selection === 'service') {
        this.clearAvailableDemServices();
      }
    })

    this.culvertSketch = new Sketch({
      id: 'culvertSketch',
      layer: this.culvertGraphicsLayer,
      view: this.mapView,
      creationMode: 'single',
      visibleElements: {
        createTools: {
          point: false,
          polyline: true,
          polygon: false,
          rectangle: false,
          circle: false,
        },
        selectionTools: {
          'rectangle-selection': true,
          'lasso-selection': true,
        },
        settingsMenu: false,
        undoRedoMenu: false,
      } as any,
    });

    this.culvertSketch.viewModel.polylineSymbol = culvertGraphicSymbol;
    this.watchHandle8 = this.culvertSketch.on('create', (event) => {
      this.aoiSketch.cancel();
      this.outletSketch.cancel();
      this.watershedSketch.cancel();
      this.flowPathSketch.cancel();
      if (event.state === 'complete') {
        this.hydrologyValidationError = false;
      }
    });
    this.watchHandle14 = this.culvertSketch.on('update', (event) => {
      this.aoiSketch.cancel();
      this.outletSketch.cancel();
      this.watershedSketch.cancel();
      this.flowPathSketch.cancel();
      if (event.state === 'complete') {
        this.hydrologyValidationError = false;
      }
    });
    this.watchHandle15 = this.culvertSketch.on('delete', (event) => {
      this.aoiSketch.cancel();
      this.outletSketch.cancel();
      this.watershedSketch.cancel();
      this.flowPathSketch.cancel();
      this.hydrologyValidationError = false;
    });

    this.outletSketch = new Sketch({
      id: 'outletSketch',
      layer: this.outletGraphicsLayer,
      view: this.mapView,
      creationMode: 'single',
      visibleElements: {
        createTools: {
          point: false,
          polyline: true,
          polygon: false,
          rectangle: false,
          circle: false,
        },
        selectionTools: {
          'rectangle-selection': true,
          'lasso-selection': true,
        },
        settingsMenu: false,
        undoRedoMenu: false,
      } as any,
    });
    this.outletSketch.viewModel.polylineSymbol = outletGraphicSymbol;
    this.watchHandle9 = this.outletSketch.on('create', (event) => this.watchHandle9CreateCallback(event));
    this.watchHandle13 = this.outletSketch.on('update', (event) => this.watchHandle13UpdateCallback(event));
    this.watershedSketch = new Sketch({
      id: 'watershedSketch',
      layer: this.watershedGraphicsLayer,
      view: this.mapView,
      creationMode: 'single',
      visibleElements: {
        createTools: {
          point: false,
          polyline: false,
          polygon: false,
          rectangle: false,
          circle: false,
        },
        selectionTools: {
          'rectangle-selection': false,
          'lasso-selection': false,
        },
        settingsMenu: false,
        undoRedoMenu: true,
      } as any,
    });

    this.watershedSketch.defaultUpdateOptions.enableRotation = false;
    this.watershedSketch.defaultUpdateOptions.enableScaling = false;
    this.watershedSketch.defaultUpdateOptions.toggleToolOnClick = false;
    this.watershedSketch.defaultUpdateOptions.tool = 'reshape';
    (this.watershedSketch.defaultUpdateOptions as any).reshapeOptions = {
      shapeOperation: 'none',
    };

    this.flowPathSketch = new SketchViewModel({
      layer: this.flowPathGraphicsLayer,
      view: this.mapView,
    });

    this.flowPathSketch.polylineSymbol = validFlowPathSymbol;
    // this.flowPathSketch.viewModel.polylineSymbol = validFlowPathSymbol;
    this.flowPathSketch.defaultUpdateOptions.enableRotation = false;
    this.flowPathSketch.defaultUpdateOptions.enableScaling = false;
    this.flowPathSketch.defaultUpdateOptions.toggleToolOnClick = false;
    this.flowPathSketch.defaultUpdateOptions.tool = 'reshape';
    (this.flowPathSketch.defaultUpdateOptions as any).reshapeOptions = {
      shapeOperation: 'none',
    };

    this.mapContainer = this.mapView.container as HTMLElement;

    this.searchBar = new Search({
      view: this.mapView,
      id: 'searchWidget',
    });

    // wireup event handlers and watch callbacks

    this.watchHandle1 = this.aoiSketch.watch('activeTool', this.activeToolWatchHandler.bind(this));
    this.watchHandle2 = this.culvertSketch.watch('activeTool', this.activeToolWatchHandler.bind(this));
    this.watchHandle3 = this.outletSketch.watch('activeTool', this.activeToolWatchHandler.bind(this));
    this.watchHandle4 = this.watershedSketch.watch('activeTool', this.activeToolWatchHandler.bind(this));

    //preventMove operations for watershed sketches

    this.watchHandle5 = this.watershedSketch.on('update', this.watershedSketchUpdateHandler_preventMove.bind(this));
    this.watchHandle6 = this.watershedSketch.on('undo', this.watershedSketchUndoHandler.bind(this));
    this.watchHandle7 = this.watershedSketch.on('redo', this.watershedSketchRedoHandler.bind(this));

    // flow path sketches
    this.watchHandle20 = this.flowPathSketch.on('update', this.flowPathSketchUpdateHandler_preventMove.bind(this));
    this.watchHandle21 = this.flowPathSketch.on('undo', (event) => this.flowPathSketchUndoHandler(event));
    this.watchHandle22 = this.flowPathSketch.on('redo', (event) => this.flowPathSketchRedoHandler(event));
    this.watchHandle23 = this.flowPathSketch.on('create', (event) => this.watchHandle23CreateCallback(event));

    //attach optional gpSessionId
    if (gpSessionId) this.gpSessionId = gpSessionId;

    //load optional gpSessionData
    if (gpSessionData) {
      // load UploadDEM results
      // this.result_dem = gpSessionData.result_dem;
      this.result_dem_boundary = gpSessionData.result_dem_boundary;

      // load DefineAOI results
      if (this.result_dem_boundary) {
        if (gpSessionData.result_aoi) {
          this.result_aoi = gpSessionData.result_aoi;
          // this.result_clipdem = gpSessionData.result_clipdem;
          // this.result_hillshade = gpSessionData.result_hillshade;
          // this.result_depthgrid = gpSessionData.result_depthgrid;
          // this.result_contours = gpSessionData.result_contours;
          // this.result_streamlines = gpSessionData.result_streamlines;
          this.__currentStepIndex = 1;
        }
        this.in_dem_service_selection = 'upload';
        // add a fake DEM to allow for the workflow to proceed
        this.result_dem = new RasterData();
      }

      // load CreateWatershed results
      if (this.result_aoi && gpSessionData.result_watershed) {
        this.result_culverts = gpSessionData.result_culverts;
        this.result_outlets = gpSessionData.result_outlets;
        this.outletsFeature = gpSessionData.result_outlets;
        this.result_watershed = gpSessionData.result_watershed;
        this.result_flowpath = gpSessionData.result_flowpath;
        this.__currentStepIndex = 2;
      }

      this.previouslyHadCulverts =
        (this.__currentStepIndex === 0 ? false :
          (this.result_culverts !== undefined) && (this.result_culverts.features.length > 0));

      // load PrepareSAL results
      if (this.result_watershed && gpSessionData.result_landuse) {
        this.output_SAL_LANDUSE = gpSessionData.result_landuse;
        if (gpSessionData.result_soils) {
          this.result_soils = gpSessionData.result_soils;
        }
        if (lulcData && undefined !== lulcData.lulc && 0 < lulcData.lulc.length) {
          this.landUseTableComplete = lulcData.complete;

          this.edit_SAL_LANDUSE_graphicsLayer = new GraphicsLayer();
          for (const g of lulcData.lulc.map((j) => Graphic.fromJSON(j))) {
            // highlights serialize incorrectly as empty objects, causing a bug
            if (g.attributes.__highlight1) g.attributes.__highlight1 = undefined;
            if (g.attributes.__highlight2) g.attributes.__highlight2 = undefined;

            this.edit_SAL_LANDUSE_graphicsLayer.graphics.push(g);
          }
        } else {
          if (this.output_SAL_LANDUSE) {
            const edit_SAL_LANDUSE_featureSet = gisUtils.cloneFeatureSet(this.output_SAL_LANDUSE!);
            this.edit_SAL_LANDUSE_graphicsLayer = gisUtils.featureSetToGraphicsLayer(
              edit_SAL_LANDUSE_featureSet,
              __editLandUseSymbol
            );
          }
        }
        if (this.edit_SAL_LANDUSE_graphicsLayer) {
          this.edit_SAL_LANDUSE_graphicsLayer.title = 'Land Use';
          this.edit_SAL_LANDUSE_graphicsLayer.id = 'salLandGraphics';
          this.edit_SAL_LANDUSE_graphicsLayer.visible = !this.landUseTableComplete;

          //todo: cleanup event handlers
          this.edit_SAL_LANDUSE_graphicsLayer.on('layerview-create', (e) => {
            this.edit_SAL_LANDUSE_graphicsLayerView = e.layerView as GraphicsLayerView;
            this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
          });
          this.edit_SAL_LANDUSE_graphicsLayer.on('layerview-destroy', (e) => {
            this.edit_SAL_LANDUSE_graphicsLayerView = undefined;
            this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
          });
        }
        this.__currentStepIndex = 3;
      }

      // load CalculateRCN results
      if (this.output_SAL_LANDUSE && this.result_soils && gpSessionData.result_rcn_subareas) {
        this.output_RCN_SUBAREAS = gpSessionData.result_rcn_subareas;
        this.output_RCN_WATERSHED = gpSessionData.result_rcn_watershed;

        if (this.output_RCN_WATERSHED &&
          this.output_RCN_WATERSHED.features &&
          (this.output_RCN_WATERSHED.features.length > 0)) {
          const rcnWatershedError = DelineateWatershedInputStateVM.validateRcnWatershedParams(this.output_RCN_WATERSHED?.features[0])
          this.hydrologyValidationError = rcnWatershedError.outOfRange;
          if (this.hydrologyValidationError) {
            console.warn(rcnWatershedError.message);
          }
          this.__currentStepIndex = 4;
          // if at Results step, flip this to true to support backing from Results to Select HSG
          // this.landUseTableComplete = true;
        }
      }

      //need to get in_dem_elevation_units from result_dem_boundary
      if (this.result_dem_boundary) {
        if (this.result_dem_boundary.spatialReference) {
          this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
        }
        const feature = this.result_dem_boundary.features[0];
        if (feature !== undefined) {
          const units: string = feature.attributes['ElevationUnits'];
          this.in_dem_elevation_units = units;
          this.demFileCellSize = feature.attributes['CellSize'];
          // console.warn('FILE CELL SIZE:', this.demFileCellSize);
          const linearUnits = feature.attributes['LinearUnits'] as GpLinearUnit;
          if (this.demFileCellSize && linearUnits) {
            this.setCellSizeStreamThreshold(this.demFileCellSize, linearUnits);
          }
        }
      }

      // routine to discover which layers to load based on which datasets were loaded by step
      // only loads layers that have matching result loaded in 'this'
      for (const resultName of ___resultNames) {
        if (!(this.hydrologyValidationError &&
          ((resultName === "result_rcn_subareas") ||
            (resultName === "result_rcn_watershed")))) {
          const resultLayerName = resultName + '_layer';
          const resultLayer = gpSessionData[resultLayerName];
          if (resultLayer) {
            if (resultName === "result_flowpath_orig") {
              resultLayer.visible = false;
            }
            this.resultsGroupLayer.add(resultLayer);
          }
        }
      }
      this.in_generate_streamlines =
        ((this.__currentStepIndex === 0) ? true : (gpSessionData.result_streamlines_layer !== undefined));
      if (this.edit_SAL_LANDUSE_graphicsLayer) {
        //hack get landuse layer where I want it, above soils layer
        this.result_soils_layer = this.resultsGroupLayer.findLayerById('result_soils') as FeatureLayer;
        if (this.result_soils_layer) {
          this.result_soils_layer.visible = this.landUseTableComplete;
          const index = this.resultsGroupLayer.layers.indexOf(this.result_soils_layer);
          this.resultsGroupLayer.add(this.edit_SAL_LANDUSE_graphicsLayer, index + 1);
        } else {
          this.resultsGroupLayer.add(this.edit_SAL_LANDUSE_graphicsLayer);
        }
      }

      // ensure outlets layer is on top
      this.ensureOutletsLayerOnTop();

      if (this.currentStepIndex === 2) {
        if (this.result_watershed) {
          this.addWatershedGraphics(this.result_watershed, true);
        }
        if (this.result_flowpath) {
          this.addFlowPathGraphics(this.result_flowpath);
        }
      }
      if (this.currentStepIndex === 3) {
        if (this.result_soils) {
          this.salSoilGraphicsLayer.removeAll();
          this.result_soils.features.forEach((g) => {
            const graphic = g.clone();
            graphic.symbol = salSoilSymbol;
            this.salSoilGraphicsLayer.add(graphic);
          });
          this.salSoilArray = JSON.parse(JSON.stringify(this.result_soils.features));
        }
      }
      if (this.currentStepIndex === 4) {
        //TODO: fix RCN backend procedure to echo user input values
        this.salSoilArray = JSON.parse(JSON.stringify(this.result_soils!.features));

        if (this.output_RCN_WATERSHED) {
          this.rcnWatershedGraphicsLayer.removeAll();
          this.output_RCN_WATERSHED.features.forEach((g) => {
            const graphic = g.clone();
            graphic.symbol = rcnWatershedSymbol;
            this.rcnWatershedGraphicsLayer.add(graphic);
          });

          this.rcnWatershedArray = JSON.parse(JSON.stringify(this.output_RCN_WATERSHED.features));
        }
        if (this.output_RCN_SUBAREAS) {
          this.rcnSubareasArray = JSON.parse(JSON.stringify(this.output_RCN_SUBAREAS.features));
        }
      }
    } else {
      // zoom to state or county extent if no gp results present
      this.zoomToSubjectArea();
    }

    // Load optional SAL table data
    if (this.currentStepIndex === 4 && loadedInput) {
      this.salSoilArray = loadedInput.soilArray;
    }

    const uploadDEMState = this.exportStateAtStep(0);
    if (uploadDEMState) {
      this.undoStack.push(uploadDEMState);
      const defineAOIState = this.exportStateAtStep(1);
      if (defineAOIState) {
        this.undoStack.push(defineAOIState);
        const createWatershedState = this.exportStateAtStep(2);
        if (createWatershedState) {
          this.undoStack.push(createWatershedState);
          const editWatershedState = this.exportStateAtStep(3);
          if (editWatershedState) {
            this.undoStack.push(editWatershedState);
            if (!this.hydrologyValidationError) {
              const prepareSalState = this.exportStateAtStep(4);
              if (prepareSalState) {
                this.undoStack.push(prepareSalState);
              }
            } else {
              this.__currentStepIndex--;
            }
          }
        }
      }
    }
    // pop the undo stack once to align with the workflow
    this.undoStack.pop();

    if (beforeActivityUpdateCallback) {
      this.beforeActivityUpdateCallback = beforeActivityUpdateCallback;
    } else {
      //provide default handler function
      this.beforeActivityUpdateCallback = (eventData: any, eventSource: any): void => { };
    }

    if (onActivityUpdateCallback) {
      this.onActivityUpdateCallback = onActivityUpdateCallback;
    } else {
      //provide default handler function
      this.onActivityUpdateCallback = async (eventData: any, eventSource: any): Promise<void> => { };
    }
    // apply preferences on load

    mapPreferences.applyPreference_LayerVisibility(this.resultsGroupLayer.layers);

    this.configDemServices = appConfig.demMapServices;
    if (stores.AppStore.userProfileStore.applicationFlags.enabledemsearch) {
      if (this.currentStepIndex === 0) {
        this.createDemImageLayers();
      }
    }

    // this.logUndoStack();
    if (this.currentStepIndex === 3) {
      this.setupSalSelection();
    }

    // turn the computed watershed layer off if showing the modified one
    this.hideResultWatershedLayer();
  } // end constructor

  private handleOutsideClick = async (event: MouseEvent): Promise<void> => {
    if (!this.mapContainer.contains(event.target as Node)) {
      this.deactivate();
    }
  };

  public async deactivate(): Promise<void> {
    document.removeEventListener('mousedown', this.handleOutsideClick);
    this.watershedSketch.cancel();
    this.flowPathSketch.cancel();
  }

  @action.bound resetHydrologyValidationError(): void {
    this.hydrologyValidationError = false;
  }

  @action.bound async createDemImageLayers() {
    this.jobExecuting = true;
    this.jobMessages = ['Loading DEM services...'];
    let message = '';
    this.errorDemServices = [];
    // const removed = this.demServicesGroupLayer.removeAll();
    // for (const lyr of removed) {
    //   lyr.destroy();
    // }
    if (this.bestAvailableHillshade) this.mapView.map.remove(this.bestAvailableHillshade);
    const currentTimeout = esriConfig.request.timeout;
    esriConfig.request.timeout = 15000;
    const maxTries = 10;
    let failed = true;
    let failedLayer: Layer | undefined;
    for (const svc of this.configDemServices) {
      let needToAdd = true;
      failedLayer = undefined;
      const foundLayer = this.demServicesGroupLayer.findLayerById(svc.id);
      if (foundLayer) {
        if (foundLayer.title.endsWith(__notAvailableSuffix)) {
          failedLayer = foundLayer;
        } else {
          needToAdd = false;
        }
      }
      if (needToAdd) {
        failed = true;
        message = message + `Loading ${svc.title}...`;
        this.jobMessages = [message];
        const startTime = Date.now();
        let imgLyr: ImageryLayer | undefined;
        for (let i = 1; ; i++) {
          try {
            imgLyr = new ImageryLayer({
              ...svc,
              visible: false,
              renderingRule: {
                functionName: svc.hillshadeName
              }
            });
            await imgLyr.load();

            const demServiceProps = await this.getDemServiceProperties(svc.url);
            if (demServiceProps && demServiceProps.spatialReference && demServiceProps.pixelSizeX) {
              this.demServicesProperties.push({
                ...demServiceProps,
                cellSize: DelineateWatershedInputStateVM.getDemServiceCellSize(demServiceProps),
                id: svc.id as string,
                title: svc.title as string,
                url: svc.url as string
              });
            }
            if (failedLayer) {
              this.demServicesGroupLayer.remove(failedLayer);
              failedLayer.destroy();
              failedLayer = undefined;
            }
            this.demServicesGroupLayer.add(imgLyr);
            imgLyr = undefined;
            failed = false;
            break;
          } catch (error) {
            if (i >= maxTries) {
              if (imgLyr) {
                if (failedLayer) {
                  imgLyr.destroy();
                } else {
                  imgLyr.title = svc.title + __notAvailableSuffix;
                  this.demServicesGroupLayer.add(imgLyr);
                }
                imgLyr = undefined;
              }
              this.errorDemServices.push(svc.title);
              break;
            } else {
              if (imgLyr) {
                imgLyr.destroy();
                imgLyr = undefined;
              }
            }
          }
        }
        const elapsedTime = Math.ceil((Date.now() - startTime) / 1000.0);
        message = `${svc.title}\n${failed ? 'failed to load' : 'loaded'} in ${elapsedTime} second${elapsedTime > 1 ? 's' : ''}. `;
        this.jobMessages = [message];
      }
    }
    if (this.demServicesGroupLayer.findLayerById('usdaNedBestAvailable')) {
      this.bestAvailableHillshade = this.demServicesGroupLayer.findLayerById('usdaNedBestAvailable') as ImageryLayer;
      this.demServicesGroupLayer.remove(this.bestAvailableHillshade);
      this.mapView.map.add(this.bestAvailableHillshade!, 0);
    }
    esriConfig.request.timeout = currentTimeout;
    this.jobExecuting = false;
    if (this.errorDemServices.length > 0) {
      MessageCenter.addWarning('Failed to load the following DEM services:\n' + this.errorDemServices.join(',\n'));
    }
  }

  @action.bound syncSoilSelection(): void {
    if (this.salSoilGraphicsLayer) {
      const oidFieldName = 'OBJECTID';
      const swapGraphics: Graphic[] = [];
      for (const g of this.salSelectGraphicsLayer.graphics) {
        const idx = this.selectedSoils.findIndex(id => { return id === g.attributes[oidFieldName]; });
        if (idx < 0) {
          swapGraphics.push(g);
        }
      }
      if (swapGraphics.length > 0) {
        this.salSelectGraphicsLayer.graphics.removeMany(swapGraphics);
        swapGraphics.splice(0, swapGraphics.length);
      }
      const graphics = this.salSelectGraphicsLayer.graphics.toArray();
      const sourceGraphics = this.salSoilGraphicsLayer.graphics.toArray();
      for (const id of this.selectedSoils) {
        const idx = graphics.findIndex(g => { return id === g.attributes[oidFieldName]; });
        if (idx < 0) {
          const source = sourceGraphics.find(g => { return id === g.attributes[oidFieldName]; });
          if (source) {
            const copy = new Graphic({
              geometry: source.geometry.clone(),
              attributes: {},
              symbol: _selectSALSymbol
            });
            copy.attributes[oidFieldName] = id;
            swapGraphics.push(copy);
          }
        }
      }
      if (swapGraphics.length > 0) {
        this.salSelectGraphicsLayer.graphics.addMany(swapGraphics);
      }
    }
  }

  @action.bound syncLandUseSelection(): void {
    if (this.output_SAL_LANDUSE && this.edit_SAL_LANDUSE_graphicsLayer) {
      const oidField = this.output_SAL_LANDUSE.fields.find((f) => {
        return f.type === 'oid';
      });
      const swapGraphics: Graphic[] = [];
      for (const g of this.salSelectGraphicsLayer.graphics) {
        const idx = this.selectedLandUse.findIndex(l => { return l[oidField!.name] === g.attributes[oidField!.name]; });
        if (idx < 0) {
          swapGraphics.push(g);
        }
      }
      if (swapGraphics.length > 0) {
        this.salSelectGraphicsLayer.graphics.removeMany(swapGraphics);
      }
      swapGraphics.splice(0, swapGraphics.length);
      const graphics = this.salSelectGraphicsLayer.graphics.toArray();
      const sourceGraphics = this.edit_SAL_LANDUSE_graphicsLayer.graphics.toArray();
      for (const l of this.selectedLandUse) {
        const idx = graphics.findIndex(g => { return l[oidField!.name] === g.attributes[oidField!.name]; });
        if (idx < 0) {
          const source = sourceGraphics.find(g => { return l[oidField!.name] === g.attributes[oidField!.name]; });
          if (source) {
            const copy = new Graphic({
              geometry: source.geometry.clone(),
              attributes: {},
              symbol: _selectSALSymbol
            });
            copy.attributes[oidField!.name] = l[oidField!.name];
            swapGraphics.push(copy);
          }
        }
      }
      if (swapGraphics.length > 0) {
        this.salSelectGraphicsLayer.graphics.addMany(swapGraphics);
      }
    }
  }

  public forceSyncSoilSelection() {
    if (this.salHitTargetLayer && (this.salHitTargetLayer.id === 'soilGraphicsLayer')) {
      this.editSoilVm?.syncSoilSelection(this.selectedSoils);
    }
  }

  setupSalSelection() {
    if (!this.salHitTargetLayer) return;
    if (this.salHitTargetLayer.id !== 'soilGraphicsLayer') {
      this.syncLandUseSelection();
    }

    const oidFieldName = 'OBJECTID';

    this.watchHandle16 = this.mapView.on('click', (async (e: __esri.ViewClickEvent) => {
      if ((e.button === 0) && this.salHitTargetLayer) {
        const isSoil = (this.salHitTargetLayer.id === 'soilGraphicsLayer');
        const shiftDown = e.native.ctrlKey || e.native.shiftKey;
        let result: Graphic | undefined = undefined;
        if (this.salHitTargetLayer.graphics.length > 0) {
          const targetCrs = this.salHitTargetLayer.graphics.at(0).geometry.spatialReference;
          if (!esriProjection.isLoaded()) {
            await esriProjection.load();
          }
          const p = esriProjection.project(e.mapPoint, targetCrs) as Point;
          result = this.salHitTargetLayer.graphics.toArray().find(f => {
            if (p.x >= f.geometry.extent.xmin &&
              p.x <= f.geometry.extent.xmax &&
              p.y >= f.geometry.extent.ymin &&
              p.y <= f.geometry.extent.ymax) {
              return (f.geometry as Polygon).contains(p);
            }
            return false;
          });
        }
        if (isSoil) {
          if (result) {
            const idx = this.selectedSoils.findIndex(id => { return id === result!.attributes[oidFieldName]; });
            if (idx < 0) {
              if (!shiftDown) {
                this.selectedSoils.splice(0, this.selectedSoils.length);
              }
              this.selectedSoils.push(result.attributes[oidFieldName]);
            } else {
              if (shiftDown) {
                this.selectedSoils.splice(idx, 1);
              } else {
                this.selectedSoils.splice(0, idx);
                this.selectedSoils.splice(1, this.selectedSoils.length - 1);
              }
            }
          } else {
            if (!shiftDown) {
              this.selectedSoils.splice(0, this.selectedSoils.length);
            }
          }
          this.editSoilVm?.syncSoilSelection(this.selectedSoils);
        } else {
          if (result) {
            const idx = this.selectedLandUse.findIndex(l => { return l[oidFieldName] === result!.attributes[oidFieldName]; });
            if (idx < 0) {
              if (!shiftDown) {
                this.selectedLandUse.splice(0, this.selectedLandUse.length);
              }
              this.selectedLandUse.push(result.attributes);
            } else {
              if (shiftDown) {
                this.selectedLandUse.splice(idx, 1);
              } else {
                this.selectedLandUse.splice(0, idx);
                this.selectedLandUse.splice(1, this.selectedLandUse.length - 1);
              }
            }
          } else {
            if (!shiftDown) {
              this.selectedLandUse.splice(0, this.selectedLandUse.length);
            }
          }
          this.syncLandUseSelection();
        }
      }
    }));

    this.watchHandle17 = this.mapView.on('pointer-move', (async (e: __esri.ViewPointerMoveEvent) => {
      if (this.salHitTargetLayer) {
        const mapPoint = this.mapView.toMap({ x: e.x, y: e.y });
        let result: Graphic | undefined = undefined;
        if (this.salHitTargetLayer.graphics.length > 0) {
          const targetCrs = this.salHitTargetLayer.graphics.at(0).geometry.spatialReference;
          if (!esriProjection.isLoaded()) {
            await esriProjection.load();
          }
          const p = esriProjection.project(mapPoint, targetCrs) as Point;
          result = this.salHitTargetLayer.graphics.toArray().find(f => {
            if (p.x >= f.geometry.extent.xmin &&
              p.x <= f.geometry.extent.xmax &&
              p.y >= f.geometry.extent.ymin &&
              p.y <= f.geometry.extent.ymax) {
              return (f.geometry as Polygon).contains(p);
            }
            return false;
          });
        }
        if (result) {
          if (result.attributes[oidFieldName] !== this.highlightOid) {
            let highlightView = this.edit_SAL_LANDUSE_graphicsLayerView;
            let prefix = 'lulc-table-row-';
            if (this.salHitTargetLayer.id === 'soilGraphicsLayer') {
              this.salHitTargetLayer.visible = true;
              highlightView = this.salSoilGraphicsLayerView;
              prefix = 'soil-table-row-';
            }
            this.clearHighlight(this.salHitTargetLayer, oidFieldName);
            const handle = highlightView?.highlight(result);
            result.attributes.__highlight1 = handle;
            this.highlightOid = result.attributes[oidFieldName];

            const rowId = `${prefix}${result.attributes[oidFieldName]}`;
            const element = document.getElementById(rowId);
            if (element) {
              element.classList.add("highlighted-row");
            }
          }
        } else {
          this.clearHighlight(this.salHitTargetLayer, oidFieldName);
        }
      }
    }));

    this.watchHandle18 = this.mapView.on('pointer-leave', (async (e: __esri.ViewPointerLeaveEvent) => {
      this.clearHighlight(this.salHitTargetLayer, oidFieldName);
    }));

    this.salSelectGraphicsLayer.visible = true;
    this.salSelectionSetup = true;
  }

  public clearHighlight(targetLayer: GraphicsLayer | undefined, oidFieldName: string | undefined = 'OBJECTID') {
    if ((this.highlightOid > -1) && targetLayer) {
      const graphic = targetLayer.graphics.toArray().find(g => {
        return this.highlightOid === g.attributes[oidFieldName];
      });
      if (graphic) {
        let handle = graphic.attributes.__highlight1;
        if (handle) {
          handle.remove();
          graphic.attributes.__highlight1 = undefined;
          this.highlightOid = -1;
        }

        const prefix = (targetLayer.id === 'soilGraphicsLayer' ? 'soil-table-row-' : 'lulc-table-row-');
        const rowId = prefix + graphic.attributes['OBJECTID'];
        const element = document.getElementById(rowId);
        if (element) {
          element.classList.remove("highlighted-row");
        }
      }
    }
  }

  public highlightSoil(id: number) {
    if ((id !== this.highlightOid) && this.salHitTargetLayer &&
      (this.salHitTargetLayer.id === 'soilGraphicsLayer')) {
      this.clearHighlight(this.salHitTargetLayer);
      const graphic = this.salHitTargetLayer.graphics.toArray().find(g => {
        return id === g.attributes['OBJECTID'];
      });
      if (graphic) {
        this.salHitTargetLayer.visible = true;
        const handle = this.salSoilGraphicsLayerView?.highlight(graphic);
        graphic.attributes.__highlight1 = handle;
        this.highlightOid = graphic.attributes['OBJECTID'];
      }
    }
  }

  public removeHighlightSoil(id: number) {
    if ((id === this.highlightOid) && this.salHitTargetLayer &&
      (this.salHitTargetLayer.id === 'soilGraphicsLayer')) {
      const graphic = this.salHitTargetLayer.graphics.toArray().find(g => {
        return id === g.attributes['OBJECTID'];
      });
      if (graphic) {
        let handle = graphic.attributes.__highlight1;
        if (handle) {
          handle.remove();
          graphic.attributes.__highlight1 = undefined;
          this.highlightOid = -1;
        }
      }
    }
  }

  unsetSalSelection() {
    this.clearHighlight(this.salSoilGraphicsLayer);
    this.clearHighlight(this.edit_SAL_LANDUSE_graphicsLayer);
    this.watchHandle16?.remove();
    this.watchHandle17?.remove();
    this.watchHandle18?.remove();
    this.salSelectGraphicsLayer.visible = false;
    this.salSoilGraphicsLayer.visible = false;
    this.salSelectionSetup = false;
  }

  handleStepChange() {
    if (this.__currentStepIndex === 3) {
      if (!this.salSelectionSetup) this.setupSalSelection();
    } else {
      if (this.salSelectionSetup) this.unsetSalSelection();
    }
  }

  get salHitTargetLayer(): GraphicsLayer | undefined {
    if (this.__currentStepIndex === 3) {
      let hitLayer: GraphicsLayer | undefined = undefined;
      if (this.salSoilGraphicsLayer && this.result_soils_layer && this.result_soils_layer.visible) {
        hitLayer = this.salSoilGraphicsLayer;
      } else if (this.edit_SAL_LANDUSE_graphicsLayer && this.edit_SAL_LANDUSE_graphicsLayer.visible) {
        hitLayer = this.edit_SAL_LANDUSE_graphicsLayer;
      }
      return hitLayer;
    }
    return undefined;
  }

  @action.bound mergeSelectedLandusePolygons(): void {
    if (this.output_SAL_LANDUSE && this.edit_SAL_LANDUSE_graphicsLayer && this.selectedLandUse.length > 0) {
      const oidField = this.output_SAL_LANDUSE.fields.find((f) => {
        return f.type === 'oid';
      });
      const oldGraphics: Graphic[] = [];
      for (const item of this.selectedLandUse) {
        const graphic = this.edit_SAL_LANDUSE_graphicsLayer.graphics.find((e) => {
          return e.attributes === item;
        });
        if (graphic) {
          oldGraphics.push(graphic);
        }
      }
      const polygons = oldGraphics.map((graphics) => {
        return graphics.geometry as Polygon;
      });
      const polygon = esriGeometryEngine.union(polygons) as Polygon;

      const newGraphic = new Graphic(); // oldGraphics[0].clone();
      newGraphic.attributes = {};
      newGraphic.setAttribute(oidField!.name, oldGraphics[0].attributes[oidField!.name]);
      //reset fields (except OID)
      for (const field of this.output_SAL_LANDUSE.fields) {
        if (field.type !== 'oid') {
          newGraphic.attributes[field.name] = undefined;
        }
      }
      newGraphic.symbol = __editLandUseSymbol;
      newGraphic.geometry = polygon;
      newGraphic.attributes.Acres = gisUtils.computeAcres(polygon);

      const index = this.edit_SAL_LANDUSE_graphicsLayer.graphics.indexOf(oldGraphics[0]);
      this.edit_SAL_LANDUSE_graphicsLayer.graphics.add(newGraphic, index);

      this.edit_SAL_LANDUSE_graphicsLayer.removeMany(oldGraphics);

      this.selectedLandUse.splice(0, this.selectedLandUse.length);
      this.salSelectGraphicsLayer.graphics.removeAll();

      this.hydrologyValidationError = false;
    }
  }
  @action.bound resetLandusePolygons(): void {
    if (this.output_SAL_LANDUSE && this.edit_SAL_LANDUSE_graphicsLayer) {
      this.salSelectGraphicsLayer.graphics.removeAll();
      const featureSet = gisUtils.cloneFeatureSet(this.output_SAL_LANDUSE);
      const newFeatures = featureSet.features;
      for (const newFeature of newFeatures) {
        newFeature.symbol = __editLandUseSymbol;
      }
      this.edit_SAL_LANDUSE_graphicsLayer.removeAll();
      this.edit_SAL_LANDUSE_graphicsLayer.addMany(newFeatures);
      this.selectedLandUse.splice(0, this.selectedLandUse.length);

      this.hydrologyValidationError = false;
    }
  }

  @computed get aoiGraphicsCount(): number {
    return this.aoiGraphicsLayer.graphics.length;
  }
  @computed get culvertGraphicsCount(): number {
    return this.culvertGraphicsLayer.graphics.length;
  }
  @computed get outletGraphicsCount(): number {
    return this.outletGraphicsLayer.graphics.length;
  }
  @computed get watershedGraphicsCount(): number {
    return this.watershedGraphicsLayer.graphics.length;
  }
  @computed get flowPathGraphicsCount(): number {
    return this.flowPathGraphicsLayer.graphics.length;
  }
  @computed get hasAoiGraphics(): boolean {
    return this.aoiGraphicsLayer.graphics.length > 0;
  }
  @computed get hasCulvertGraphics(): boolean {
    return this.culvertGraphicsLayer.graphics.length > 0;
  }
  @computed get hasOutletGraphics(): boolean {
    return this.outletGraphicsLayer.graphics.length > 0;
  }
  @computed get hasWatershedGraphics(): boolean {
    return this.watershedGraphicsLayer.graphics.length > 0;
  }
  @computed get hasFlowPathGraphics(): boolean {
    return this.flowPathGraphicsLayer.graphics.length > 0;
  }
  @computed get hasValidAoiGraphics(): boolean {
    // check to make sure there is only one AOI graphic
    if (this.aoiGraphicsCount !== 1) {
      return false;
    }
    if (this.in_dem_service_selection !== 'service' && !this.result_dem_boundary) {
      return false;
    }

    let allGraphicsAreValid = true;
    if (this.in_dem_service_selection !== 'service') {
      const innerGraphics = this.aoiGraphicsLayer.graphics;
      const outerGraphics = this.result_dem_boundary!.features;

      //check to make sure each watershed is fully contained by at least one aoi
      innerGraphics.forEach((inner) => {
        let thisGraphicIsValid = false;
        outerGraphics.forEach((outer) => {
          const contains = this.containsProjectedGeometry(outer.geometry, inner.geometry);
          if (contains) {
            thisGraphicIsValid = true;
          }
        });
        if (!thisGraphicIsValid) {
          allGraphicsAreValid = false;
        }
      });
    }
    return allGraphicsAreValid;
  }
  @computed get hasValidOutletGraphics(): boolean {
    // check to make sure there is only one outlet graphic
    if (this.outletGraphicsCount !== 1) {
      return false;
    }
    // check to make sure there is at least one aoi graphic to compare against
    if (!this.result_aoi && this.aoiGraphicsCount === 0) {
      return false;
    }

    const innerGraphics = this.outletGraphicsLayer.graphics;
    const outerGraphics = this.result_aoi?.features || this.aoiGraphicsLayer.graphics; // prefer result_aoi.features if available

    //check to make sure each watershed is fully contained by at least one aoi
    let allGraphicsAreValid = true;
    innerGraphics.forEach((inner) => {
      let thisGraphicIsValid = false;
      outerGraphics.forEach((outer) => {
        const contains = this.containsProjectedGeometry(outer.geometry, inner.geometry);
        if (contains) {
          thisGraphicIsValid = true;
        }
      });
      if (!thisGraphicIsValid) {
        allGraphicsAreValid = false;
      }
    });
    return allGraphicsAreValid;
  }
  @computed get hasValidWatershedGraphics(): boolean {
    // check to make sure there is only one watershed graphic
    if (this.watershedGraphicsCount !== 1) {
      return false;
    }
    if (!this.result_aoi) {
      return false;
    }

    const innerGraphics = this.watershedGraphicsLayer.graphics;
    const outerGraphics = this.result_aoi.features;

    //check to make sure each watershed is fully contained by at least one aoi
    let allGraphicsAreValid = true;
    innerGraphics.forEach((inner) => {
      let thisGraphicIsValid = false;
      outerGraphics.forEach((outer) => {
        const contains = this.containsProjectedGeometry(outer.geometry, inner.geometry);
        const correct = this.isTopologicallyCorrect(inner.geometry as Polygon);
        if (contains && correct) {
          thisGraphicIsValid = true;
        }
      });
      if (!thisGraphicIsValid) {
        allGraphicsAreValid = false;
      }
    });
    return allGraphicsAreValid;
  }

  @computed get salLandArray(): any[] {
    let result: Graphic[] = [];
    if (this.edit_SAL_LANDUSE_graphicsLayer) {
      DelineateWatershedInputStateVM.ensureUniqueOids(this.edit_SAL_LANDUSE_graphicsLayer.graphics.toArray());
      result = this.edit_SAL_LANDUSE_graphicsLayer.graphics.toArray();
    }
    return result;
  }

  @computed get canSubmit(): boolean {
    if (!this.jobExecuting) {
      return true;
    } else {
      return false;
    }
  }
  get currentStepIndex(): number {
    return this.__currentStepIndex;
  }

  @computed get currentStepName(): string {
    const step = this.currentStepIndex;
    if (step < 0 || step > 4) {
      throw new Error('Unexpected result.');
    }

    if (step === 3) {
      if (!this.landUseTableComplete) {
        return this.stepNames[step];
      } else {
        return this.stepNames[step + 1];
      }
    } else if (step > 3) {
      return this.stepNames[step + 1];
    }

    return this.stepNames[step];
  }

  public get stepNames(): Readonly<string[]> {
    return workflowStepNames;
  }

  public mountLayers(): void {
    //this will result in layers being added to top of the stack
    this.unmountLayers();
    this.mapView.map.add(this.demServicesGroupLayer);
    this.mapView.map.add(this.resultsGroupLayer);
    this.mapView.map.add(this.measuresGroupLayer);
    this.mapView.map.add(this.drawingsGroupLayer);
    this.mapView.map.add(this.sketchGroupLayer);
    this.mapView.map.add(this.salSelectGraphicsLayer);
  }
  public unmountLayers(): void {
    //not necessary to check if already in map
    this.mapView.map.remove(this.measuresGroupLayer);
    this.mapView.map.remove(this.drawingsGroupLayer);
    this.mapView.map.remove(this.salSelectGraphicsLayer);
    this.mapView.map.remove(this.demServicesGroupLayer);
    this.mapView.map.remove(this.resultsGroupLayer);
    this.mapView.map.remove(this.sketchGroupLayer);
  }

  public async zoomToSubjectArea(): Promise<void> {
    // TODO: update gp services to have consistent output names, then refactor this code
    const result_watershed_layer = this.resultsGroupLayer.findLayerById('result_watershed');
    const result_aoi_layer = this.resultsGroupLayer.findLayerById('result_aoi');
    const result_dem_boundary_layer = this.resultsGroupLayer.findLayerById('result_dem_boundary');

    //zoom to layer of interest, choose by descending order of interest
    let zoomLayer = result_watershed_layer || result_aoi_layer || result_dem_boundary_layer;
    let useLayer: boolean = true;
    // use AOI graphics if about to export the DEM data from service
    if (
      zoomLayer === undefined &&
      this.currentStepIndex === 0 &&
      this.in_dem_service_selection === 'service' &&
      this.aoiGraphicsLayer.graphics.length > 0
    ) {
      zoomLayer = this.aoiGraphicsLayer;
      useLayer = false;
    }
    if (zoomLayer) {
      //cannot access zoomLayer.fullExtent until after layer completes loading (aka "when")
      zoomLayer.when(async () => {
        const origZoomExtent = useLayer ? zoomLayer.fullExtent : this.aoiGraphicsLayer.graphics.at(0).geometry.extent;
        // let zoomExtent: Geometry;
        // if (this.mapView.spatialReference.equals(origZoomExtent.spatialReference)) {
        //   zoomExtent = origZoomExtent;
        // } else {
        await esriProjection.load();
        const zoomExtent = esriProjection.project(origZoomExtent, this.mapView.spatialReference) as Geometry;
        // }
        // this.mapView.goTo(zoomExtent);

        //zooming to viewpoint ensures that rotation orientation is reset back to 0
        const viewpoint = new Viewpoint();
        viewpoint.targetGeometry = zoomExtent;
        viewpoint.rotation = 0;
        this.mapView.goTo(viewpoint);
      });
    } else {
      if (this.countyExtent === undefined) {
        await this.getProjectCountyExtent();
        if (this.countyExtent !== undefined) {
          if (stores.AppStore.efh2Store.homeWidget) {
            stores.AppStore.efh2Store.homeWidget!.viewpoint = new Viewpoint({
              targetGeometry: this.countyExtent,
              rotation: 0,
            });
          }
        }
      }
      if (this.countyExtent !== undefined) {
        //zooming to viewpoint ensures that rotation orientation is reset back to 0
        const viewpoint = new Viewpoint();
        viewpoint.targetGeometry = this.countyExtent;
        viewpoint.rotation = 0;
        this.mapView.goTo(viewpoint);
      }
    }
  }

  private async aoiSketchUpdateHandler(e: __esri.SketchUpdateEvent) {
    // console.warn(this.aoiModified ? 'MODIFIED' : 'UUUUUUUUUUUUUU');
    // console.warn('AOI EVENT:', e.state);
    let correctTopology: boolean = false;

    if (e.graphics[0] !== null) {
      const area = esriGeometryEngine.geodesicArea(e.graphics[0].geometry as Polygon, "acres");
      this.aoiArea = area.toFixed(2);
    }
    if (this.in_dem_service_selection === 'service') {
      if ((e.state === 'complete')) {
        correctTopology = this.isTopologicallyCorrect(e.graphics[0].geometry as Polygon, false);
        e.graphics[0].symbol = correctTopology ? aoiGraphicSymbol : invalidWatershedSymbol;
        efh2Store.hasValidAoiPolygon = correctTopology;
        if (this.aoiModified) await this.checkDemServicesAvailability();
      } else if (e.state !== 'start') {
        this.clearAvailableDemServices();
      }
    }
    if (e.state !== 'start' && !this.aoiModified) {
      // console.log('AOI Sketch Update Handler');
      this.aoiModified = true;
    }
  }

  @action.bound clearAvailableDemServices() {
    this.availableDemServices.splice(0);
    this.isDemServiceAvailable = false;
    this.in_dem_id = '';
    this.in_dem_service_url = '';
    this.in_dem_title = '';
  }

  @action.bound async tryDemServicesAvailability() {
    if (this.hasAoiGraphics) {
      this.checkDemServicesAvailability();
    }
  }

  @action.bound
  async checkDemServicesAvailability() {
    if (this.suppressDemServiceLoading) {
      // this.suppressDemServiceLoading = false;
      return;
    }
    this.suppressDemServiceLoading = true;
    this.clearAvailableDemServices();
    if (this.hasAoiGraphics) {
      // this.in_dem_service_selection = this.in_dem_service_selection === 'service' ? undefined : 'service';
      const graphic = this.aoiGraphicsLayer.graphics.getItemAt(0);
      if (graphic) {
        //cancel any outstanding drawing inputs
        this.aoiSketch.cancel();
        await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
        this.disableSketchControls();

        const aoi = graphic.geometry as Polygon;
        const centroid = aoi.centroid;

        const aoiUtm = await DelineateWatershedInputStateVM.getAoiBBoxInUtm(aoi);

        for (const imgLyr of this.demServicesGroupLayer.layers) {
          if (!(imgLyr.title.endsWith(__notAvailableSuffix))) {
            try {
              const imageUrl = (imgLyr as ImageryLayer).url;
              // console.log('IMAGE URL:', imageUrl);
              const identifyResult = await this.identifyDemValue(imageUrl, centroid);
              if (identifyResult !== undefined) {
                // console.log('IDENTIFY RESULT:', identifyResult.value);
                if ((identifyResult.value !== undefined) && (identifyResult.value !== 'NoData')) {
                  const numValue = Number(identifyResult.value);
                  if (!(isNaN(numValue))) {
                    if (numValue > -9990) {
                      const found = this.configDemServices.find((s) => s.id === imgLyr.id);
                      if (found) {
                        let demServiceProps = this.demServicesProperties.find((dsp) => { return dsp.id === found.id; });
                        if (!demServiceProps) {
                          demServiceProps = await this.getDemServiceProperties(imageUrl);
                          if (demServiceProps && demServiceProps.spatialReference && demServiceProps.pixelSizeX) {
                            this.demServicesProperties.push({
                              ...demServiceProps,
                              cellSize: DelineateWatershedInputStateVM.getDemServiceCellSize(demServiceProps),
                              id: found.id as string,
                              title: imgLyr.title,
                              url: imageUrl
                            });
                          }
                        }
                        if (demServiceProps && demServiceProps.spatialReference && demServiceProps.pixelSizeX) {
                          const availableDemService = DelineateWatershedInputStateVM.getAvailableDemService(demServiceProps, aoiUtm);
                          this.availableDemServices.push(availableDemService);
                          this.isDemServiceAvailable = true;
                        }
                      }
                    }
                  }
                }
              }
            } catch (error) {
              console.warn(`${imgLyr.id} has error!`);
            }
          }
        }
        this.enableSketchControls();
        if (!this.isDemServiceAvailable) {
          await MessageCenter.addWarningFromCode('Hydro_54', [
            {
              callback: () => {
                MessageCenter.clearMessages();
                this.cancel();
              },
              text: 'Cancel',
            },
          ]);
        }
      }

      // this.availableDemServices = this.configDemServices.map((i) => i);
    } else {
      this.in_dem_service_selection = undefined;
      await MessageCenter.addWarningFromCode('Hydro_71', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
    }
    this.suppressDemServiceLoading = false;
  }

  private watershedSketchRedoHandler(e: __esri.SketchRedoEvent) {
    if (e.tool === 'reshape' && e.type === 'redo') {
      const contains = this.containsProjectedGeometry(this.result_aoi!.features[0].geometry, e.graphics[0].geometry);
      const correct = this.isTopologicallyCorrect(e.graphics[0].geometry as Polygon);
      e.graphics[0].symbol = !contains ? invalidWatershedSymbol : (correct ? validWatershedSymbol : badTopologyWatershedSymbol);
      if (contains) {
        efh2Store.aoiContainsWatershed = true;
      } else {
        efh2Store.aoiContainsWatershed = false;
      }
      efh2Store.hasValidWatershedPolygon = correct;
    }
  }
  private watershedSketchUndoHandler(e: __esri.SketchUndoEvent) {
    if (e.tool === 'reshape' && e.type === 'undo') {
      const contains = this.containsProjectedGeometry(this.result_aoi!.features[0].geometry, e.graphics[0].geometry);
      const correct = this.isTopologicallyCorrect(e.graphics[0].geometry as Polygon);
      e.graphics[0].symbol = !contains ? invalidWatershedSymbol : (correct ? validWatershedSymbol : badTopologyWatershedSymbol);
      if (contains) {
        efh2Store.aoiContainsWatershed = true;
      } else {
        efh2Store.aoiContainsWatershed = false;
      }
      efh2Store.hasValidWatershedPolygon = correct;
    }
  }
  private preventMoveGeometry: any = undefined;
  private originalGeometry: any = undefined;
  private watershedSketchUpdateHandler_preventMove(e: __esri.SketchUpdateEvent) {
    if (e.state === 'start') {
      this.originalGeometry = e.graphics[0].geometry.clone();
    }

    // these conditions makes for nice preventMove behavior
    if (e.toolEventInfo && e.toolEventInfo.type === 'move-start') {
      this.preventMoveGeometry = e.graphics[0].geometry.clone();
    } else if (e.toolEventInfo && e.toolEventInfo.type === 'move') {
      e.graphics[0].geometry = this.preventMoveGeometry.clone();
    } else if (
      e.toolEventInfo &&
      (e.toolEventInfo.type === 'reshape-stop' || e.toolEventInfo.type === 'vertex-remove')
    ) {
      const contains = this.containsProjectedGeometry(this.result_aoi!.features[0].geometry, e.graphics[0].geometry);
      const correct = this.isTopologicallyCorrect(e.graphics[0].geometry as Polygon);
      e.graphics[0].symbol = !contains ? invalidWatershedSymbol : (correct ? validWatershedSymbol : badTopologyWatershedSymbol);
      if (contains) {
        efh2Store.aoiContainsWatershed = true;
      } else {
        efh2Store.aoiContainsWatershed = false;
      }
      efh2Store.hasValidWatershedPolygon = correct;
      this.hydrologyValidationError = false;
    }

    if (e.state === 'complete') {
      const currentGeom = e.graphics[0].geometry;
      const originalGeom = this.originalGeometry;

      // compare if geometries are different before setting modified = true
      if (!esriGeometryEngine.equals(currentGeom, originalGeom)) {
        this.watershedModified = true;
      }
      this.originalGeometry = undefined;
    }
  }

  private disableSketchControls() {
    this.aoiSketch.layer = undefined as any;
    this.aoiSketch.view = undefined as any;
    this.culvertSketch.layer = undefined as any;
    this.culvertSketch.view = undefined as any;
    this.outletSketch.layer = undefined as any;
    this.outletSketch.view = undefined as any;
    this.watershedSketch.layer = undefined as any;
    this.watershedSketch.view = undefined as any;
    this.flowPathSketch.layer = undefined as any;
    this.flowPathSketch.view = undefined as any;
  }

  private enableSketchControls() {
    this.aoiSketch.layer = this.aoiGraphicsLayer;
    this.aoiSketch.view = this.mapView;
    this.culvertSketch.layer = this.culvertGraphicsLayer;
    this.culvertSketch.view = this.mapView;
    this.outletSketch.layer = this.outletGraphicsLayer;
    this.outletSketch.view = this.mapView;
    this.watershedSketch.layer = this.watershedGraphicsLayer;
    this.watershedSketch.view = this.mapView;
    this.flowPathSketch.layer = this.flowPathGraphicsLayer;
    this.flowPathSketch.view = this.mapView;
  }

  public activeToolWatchHandler = (newValue: any, oldValue: any, propertyName: any, target: any) => {
    if (newValue) {
      //if a new tool is set on one Sketch widget, cancel any other operations
      if (target !== this.aoiSketch) {
        this.aoiSketch.cancel();
        this.aoiSketch.layer = undefined as any;
        this.aoiSketch.view = undefined as any;
      }
      if (target !== this.culvertSketch) {
        this.culvertSketch.cancel();
        this.culvertSketch.layer = undefined as any;
        this.culvertSketch.view = undefined as any;
      }
      if (target !== this.outletSketch) {
        this.outletSketch.cancel();
        this.outletSketch.layer = undefined as any;
        this.outletSketch.view = undefined as any;
      }
      if (target !== this.watershedSketch) {
        this.watershedSketch.cancel();
        this.watershedSketch.layer = undefined as any;
        this.watershedSketch.view = undefined as any;
      }
      if (target !== this.flowPathSketch) {
        this.flowPathSketch.cancel();
        this.flowPathSketch.layer = undefined as any;
        this.flowPathSketch.view = undefined as any;
      }
    } else {
      this.aoiSketch.layer = this.aoiGraphicsLayer;
      this.aoiSketch.view = this.mapView;
      this.culvertSketch.layer = this.culvertGraphicsLayer;
      this.culvertSketch.view = this.mapView;
      this.outletSketch.layer = this.outletGraphicsLayer;
      this.outletSketch.view = this.mapView;
      this.watershedSketch.layer = this.watershedGraphicsLayer;
      this.watershedSketch.view = this.mapView;
      this.flowPathSketch.layer = this.flowPathGraphicsLayer;
      this.flowPathSketch.view = this.mapView;
    }
  };

  private containsProjectedGeometry(outerGeometry: Geometry, innerGeometry: Geometry): boolean {
    const containsGeom1 = outerGeometry.clone();
    let containsGeom2 = innerGeometry.clone();
    if (containsGeom1.spatialReference.wkid !== containsGeom2.spatialReference.wkid) {
      containsGeom2 = esriProjection.project(containsGeom2, containsGeom1.spatialReference) as Geometry;
    }
    const contains = esriGeometryEngine.contains(containsGeom1, containsGeom2);
    return contains;
  }

  private isTopologicallyCorrect(polygon?: Polygon, displayErros: boolean = true): boolean {
    this.clearIntersectionGraphics();
    if (polygon === undefined) return false;
    if (polygon.rings === undefined) return false;
    if (polygon.rings.length !== 1) {
      // console.log('RING COUNT:', polygon.rings.length);
      if (displayErros) this.displaySmallRingsGraphics(polygon);
      return false;
    }
    if (!(polygon.isClockwise(polygon.rings[0]))) return false;
    if (polygon.isSelfIntersecting) {
      this.displayIntersectionGraphics(polygon);
      return false;
    }
    return true;
  }

  private displaySmallRingsGraphics(polygon: Polygon): void {
    if (this.intersectGraphicsLayer) {
      const intersections: Graphic[] = [];
      const areaThreshold = gisUtils.computeAcres(polygon) / 30.0;
      for (const r of polygon.rings) {
        const p = new Polygon({ rings: [r], spatialReference: polygon.spatialReference });
        const area = gisUtils.computeAcres(p);
        if (area < areaThreshold) {
          const centroid = p.centroid;
          const centroidGraphic = new Graphic({
            geometry: centroid,
            symbol: intersectionSymbol
          });
          intersections.push(centroidGraphic);
        }
      }
      if (intersections.length > 0) {
        this.intersectGraphicsLayer.graphics.addMany(intersections);
      }
    }
  }

  private clearIntersectionGraphics(): void {
    if (this.intersectGraphicsLayer) {
      this.intersectGraphicsLayer.graphics.removeAll();
    }
  }

  private displayIntersectionGraphics(polygon: Polygon): void {
    if (this.intersectGraphicsLayer) {
      const intersections: Graphic[] = [];

      for (let i = 0; i < polygon.rings[0].length - 4; i++) {
        for (let j = i + 2; j < polygon.rings[0].length - (i === 0 ? 2 : 1); j++) {
          const intersection = DelineateWatershedInputStateVM.findIntersection([polygon.rings[0][i], polygon.rings[0][i + 1]], [polygon.rings[0][j], polygon.rings[0][j + 1]], polygon.spatialReference);
          if (intersection) {
            const intersectionGraphic = new Graphic({
              geometry: intersection,
              symbol: intersectionSymbol
            });
            intersections.push(intersectionGraphic);
          }
        }
      }
      if (intersections.length > 0) {
        this.intersectGraphicsLayer.graphics.addMany(intersections);
      }
    }
  }

  private static findIntersection(s1: number[][], s2: number[][], crs: SpatialReference): Point | undefined {
    let result: Point | undefined = undefined;
    const Line1 = new Polyline({
      paths: [s1],
      spatialReference: crs
    });
    const Line2 = new Polyline({
      paths: [s2],
      spatialReference: crs
    });
    if (esriGeometryEngine.intersects(Line1, Line2)) {
      try {
        result = DelineateWatershedInputStateVM.intersectSegments(s1, s2, crs);
        // console.log('INTERSECTION:', result);
      } catch {
        result = undefined;
      }
    }

    return result;
  }

  private static intersectSegments(s1: number[][], s2: number[][], crs: SpatialReference): Point | undefined {
    let result: Point | undefined = undefined;
    let x = 0;
    let y = 0;
    let hasResult = false;

    const lp1 = DelineateWatershedInputStateVM.computeLinearParams(s1);
    const lp2 = DelineateWatershedInputStateVM.computeLinearParams(s2);

    if (lp1[2] === 0) {
      if (lp2[2] !== 0) {
        x = s1[0][0];
        const r = (s1[0][0] - s2[0][0]) / (s2[1][0] - s2[0][0]);
        y = s2[0][1] + (s2[1][1] - s2[0][1]) * r;
        hasResult = true;
      }
    } else {
      if (lp2[2] === 0) {
        x = s2[0][0];
        const r = (s2[0][0] - s1[0][0]) / (s1[1][0] - s1[0][0]);
        y = s1[0][1] + (s1[1][1] - s1[0][1]) * r;
      } else {
        x = (lp2[1] - lp1[1]) / (lp1[0] - lp2[0]);
        y = lp1[0] * x + lp1[1];
      }
      hasResult = true;
    }

    if (hasResult) {
      result = new Point({
        x: x,
        y: y,
        spatialReference: crs
      });
    }
    return result;
  }

  private static computeLinearParams(s: number[][]): number[] {
    const result: number[] = [];
    const dx = s[1][0] - s[0][0];
    if (dx === 0.0) {
      result.push(0.0);
      result.push(s[0][0]);
      result.push(0);
    } else {
      const dy = s[1][1] - s[0][1];
      const m = dy / dx;
      const c = s[0][1] - s[0][0] * m;
      result.push(m);
      result.push(c);
      result.push(1);
    }
    return result;
  }

  private async addWatershedGraphics(result_watershed: FeatureSet, updateOrigWatershed: Boolean = false) {
    this.watershedGraphicsLayer.graphics.removeAll();
    await esriProjection.load();
    result_watershed.features.forEach((graphic) => {
      //determine if aoi fully contains watershed
      const contains = this.containsProjectedGeometry(this.result_aoi!.features[0].geometry, graphic.geometry);

      //create graphic with map SRID for editing
      const g = graphic.clone();
      if (g.geometry.spatialReference.wkid !== this.mapView.spatialReference.wkid) {
        g.geometry = esriProjection.project(g.geometry, this.mapView.spatialReference) as Geometry;
        if (!g.geometry) {
          throw new Error('Unexpected result.');
        }
      }

      // update status for "contains" notifications
      if (contains && efh2Store.aoiContainsWatershed === null) efh2Store.aoiContainsWatershed = true;
      if (!contains) {
        efh2Store.aoiContainsWatershed = false;
      }

      const correct = this.isTopologicallyCorrect(g.geometry as Polygon);
      if (correct && (efh2Store.hasValidWatershedPolygon === undefined)) efh2Store.hasValidWatershedPolygon = true;
      if (!correct) efh2Store.hasValidWatershedPolygon = false;

      // update graphic symbol based on contains
      this.watershedSketch.viewModel.polygonSymbol = validWatershedSymbol;
      g.symbol = !contains ? invalidWatershedSymbol : (correct ? validWatershedSymbol : badTopologyWatershedSymbol);

      // add graphic to layer
      this.watershedGraphicsLayer.graphics.add(g);
      if (updateOrigWatershed) {
        this.originalWatershedGraphic = g.clone();
      }
    });
  }

  private async addFlowPathGraphics(result_flowPath: FeatureSet) {
    this.flowPathGraphicsLayer.graphics.removeAll();
    await esriProjection.load();
    result_flowPath.features.forEach((graphic) => {

      //create graphic with map SRID for editing
      const g = graphic.clone();
      if (g.geometry.spatialReference.wkid !== this.mapView.spatialReference.wkid) {
        g.geometry = esriProjection.project(g.geometry, this.mapView.spatialReference) as Geometry;
        if (!g.geometry) {
          throw new Error('Unexpected result.');
        }
      }
      this.updateFlowPathProperties(g);

      // add graphic to layer
      this.flowPathGraphicsLayer.graphics.add(g);
    });
  }

  @action.bound statusCallback(jobInfo: __esri.JobInfo) {
    if (jobInfo && jobInfo.messages && jobInfo.messages.length > 0) {
      const messages = jobInfo.messages
        .map((item) => {
          return item.description;
        })
        .filter((item) => {
          return item.startsWith('#MESSAGE#') || item.startsWith('Executing...') || item.startsWith('Succeeded at ');
        })
        .map((item) => {
          return item.replace('#MESSAGE#', '');
        });

      this.jobMessages = messages;
    }
  }

  @action.bound async selectDemService() {
    const demService = this.configDemServices.find((ds) => {
      return this.in_dem_id === ds.id;
    });
    if (demService) {
      this.in_dem_service_url = demService.url;
      this.in_dem_elevation_units = demService.units;
      this.in_dem_metadata_url = demService.metadata;
      this.in_dem_title = demService.title;
      this.in_dem_hillshade = demService.hillshadeName;
      this.setSelectedDemName(this.in_dem_title)
    } else {
      this.in_dem_service_url = '';
      this.in_dem_elevation_units = '';
      this.in_dem_metadata_url = '';
      this.in_dem_title = '';
      this.in_dem_hillshade = '';
    }
  }

  @action.bound previewDem() {
    if (this.in_dem_id) {
      const selectedLayer = this.demServicesGroupLayer.findLayerById(this.in_dem_id);
      if (selectedLayer) {
        const graphic = this.aoiGraphicsLayer.graphics.getItemAt(0);
        const newExent = graphic.geometry.extent;
        this.mapView.goTo(newExent);

        this.demServicesGroupLayer.layers.forEach((lyr) => (lyr.visible = false));
        selectedLayer.visible = true;
      }
    } else {
      MessageCenter.addWarning('Please select a DEM service to preview.');
    }
  }

  @action.bound disableServiceDems() { // Turns off visible service DEM layers
    if (this.bestAvailableHillshade) this.bestAvailableHillshade.visible = false;
    this.demServicesGroupLayer.layers.forEach((lyr) => (lyr.visible = false));
    this.in_dem_hillshade = "None";
  }

  @action.bound findLeftWindowBoundry() {
    if (window.screenLeft) {
      return window.screenLeft;
    }
    if (window.screenX) {
      return window.screenX;
    }

    return 0;
  }

  @action.bound findTopWindowBoundry() {
    if (window.screenTop) {
      return window.screenTop;
    }
    if (window.screenY) {
      return window.screenY;
    }

    return 0;
  }

  @action.bound openDemWindow() {
    const left = screen.width / 2 - 850 / 2 + this.findLeftWindowBoundry();
    const top = screen.height / 2 - 500 / 2 + this.findTopWindowBoundry();
    const win = window.open('', '_blank', `width= 850, height=500, top=${top}, left=${left}`);
    if (win) {
      win.document.title = `Metadata: ${this.in_dem_title}`;
      win.document.write("<!DOCTYPE html>");
      win.document.write("<html>");
      win.document.write("<head>");
      win.document.write("</head>");
      win.document.write("<body>");
      win.document.write("</body>");
      win.document.write("</body>");
      win.document.body.classList.add("external-window-scroll")

      const parentHead = window.document.querySelector("head")?.childNodes;
      parentHead?.forEach(item => {
        win.document.head.appendChild(item.cloneNode(true));
      })

      const demMetadataMarkup = win.document.createElement("div");
      win.document.body.appendChild(demMetadataMarkup);

      const header = win.document.createElement("h1");
      header.classList.add('metadata-h1');
      header.innerHTML = `Metadata: ${this.in_dem_title}`;
      demMetadataMarkup.appendChild(header);

      const table = win.document.createElement("table");
      table.classList.add('dem-metadata-table');
      demMetadataMarkup.appendChild(table);

      const thead = win.document.createElement('thead');
      table.appendChild(thead);

      const theadtr = win.document.createElement('tr');
      try {
        for (const key of Object.keys(this.demMetadataValues[0])) {
          const field = win.document.createElement('th');
          field.innerHTML = key;
          theadtr.appendChild(field);
        }
        thead.appendChild(theadtr);

        const tbody = win.document.createElement('tbody');
        table.appendChild(tbody);

        this.demMetadataValues.forEach((entry) => {
          const tabler = win.document.createElement('tr');
          for (const value of Object.values(entry)) {
            const metadataValue = (value === null) ? '' : String(value).trim();
            if (metadataValue.includes('https')) {
              const valueElement = win.document.createElement('a');
              valueElement.innerHTML = metadataValue;
              valueElement.href = metadataValue;
              valueElement.target = "_blank";
              tabler.appendChild(valueElement);
            } else {
              const valueElement = win.document.createElement('td');
              valueElement.innerHTML = metadataValue as any;
              tabler.appendChild(valueElement);
            }
            tbody.appendChild(tabler);
          }
        })
      }
      catch {
        console.log('Issue displaying metadata table')
      }
    }
  }

  private openCenteredWindow() {
    const left = screen.width / 2 - 850 / 2 + this.findLeftWindowBoundry();
    const top = screen.height / 2 - 500 / 2 + this.findTopWindowBoundry();
    window.open(`${this.in_dem_service_url}/view/Metadata`, '_blank', `width= 850, height=500, top=${top}, left=${left}`);
  }

  @action.bound openDemUrl() {
    if (this.in_dem_id) {
      if (this.in_dem_metadata_url) {
        const featureLayer = new FeatureLayer({
          url: this.in_dem_metadata_url
        });
        const query = new Query();
        query.spatialRelationship = 'intersects';
        query.where = '1=1';
        query.outFields = ['*'];
        query.geometry = this.aoiGraphicsLayer.graphics.at(0).geometry;

        featureLayer.queryFeatures(query)
          .then(
            ((featureSet) => {
              const features = featureSet.features;
              this.demMetadataValues = [...features].map(feature => {
                return feature.attributes;
              });
              if (this.demMetadataValues[0] !== undefined) {
                this.openDemWindow()
              }
              else {
                MessageCenter.addWarningFromCode('Hydro_74');
                this.openCenteredWindow();
              }
            }),
            () => {
              MessageCenter.addWarningFromCode('Hydro_66');
              this.openCenteredWindow();
            }
          );
      } else {
        this.openCenteredWindow();
      }
    } else {
      MessageCenter.addWarningFromCode('Hydro_56');
    }
  }

  @action.bound async demClip(): Promise<boolean> {
    let result: boolean = false;

    if (this.jobExecuting) {
      await MessageCenter.addWarningFromCode('Hydro_72', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      return result;
    }

    if (this.in_dem_service_url.length < 1) {
      await MessageCenter.addWarningFromCode('Hydro_65', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      return result;
    }

    if (!(this.aoiGraphicsLayer && 0 < this.aoiGraphicsLayer.graphics.length)) {
      await MessageCenter.addWarningFromCode('Hydro_71', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
      return result;
    }
    const aoiGeometry = this.aoiGraphicsLayer.graphics.getItemAt(0).geometry;

    this.demFileCellSize = undefined;

    //cancel any outstanding drawing inputs
    this.aoiSketch.cancel();
    await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
    this.disableSketchControls();

    try {
      this.jobExecuting = true;
      this.jobMessages = ['Clipping DEM...'];

      const selectedDemService = this.availableDemServices.find((ds) => {
        return this.in_dem_id === ds.id;
      });
      if (selectedDemService) {
        if (selectedDemService.notEligible) {
          await MessageCenter.addNotificationFromCode('Hydro_50', [
            {
              callback: () => {
                MessageCenter.clearMessages();
                this.cancel();
              },
              text: 'Cancel',
            },
          ]);
        } else {
          const demProps = await this.clipDemFromService(selectedDemService);
          // console.warn('CLIPPED DEM PROPERTIES:');
          // console.warn(demProps);
          if (undefined !== demProps && undefined !== demProps.href) {
            await sleep(2000);
            // the following code ensures the clipping is really complete, thus elimninates the error that occurs in clipping process.
            // also ensures wherever the ArcGIS server resides, the USDA DEM clip always works.
            // now the clipping process is stabler.
            let demRetrieved = false;
            for (let i = 0; i < 60; i++) {
              try {
                await sleep(2000); // repeatedly wait until the image export completes, and get a blob from the url.
                const resBlob = await fetch(demProps.href).then((r) => r.blob());
                if (resBlob.type === 'image/tiff') {
                  const index = (demProps.href as string).lastIndexOf('/');
                  const fileName = (demProps.href as string).substring(index + 1);
                  const file = new File([resBlob], fileName, { type: 'image/tiff' });
                  this.in_dem_file = file;
                  this.result_dem = undefined;
                  demRetrieved = true;
                  break;
                }
              } catch {
                // suppress the error
              }
            }
            if (demRetrieved) {
              this.checkInputDemUnit(aoiGeometry);
              this.setCellSizeStreamThreshold(selectedDemService.cellSize!);
              result = true;
            } else {
              await MessageCenter.addErrorFromCode('Hydro_73', [
                {
                  callback: () => {
                    MessageCenter.clearMessages();
                    this.cancel();
                  },
                  text: 'Cancel',
                },
              ]);
            }
          } else {
            await MessageCenter.addErrorFromCode('Hydro_60', [
              {
                callback: () => {
                  MessageCenter.clearMessages();
                  this.cancel();
                },
                text: 'Cancel',
              },
            ]);
          }
        }
      } else {
        await MessageCenter.addErrorFromCode('Hydro_60', [
          {
            callback: () => {
              MessageCenter.clearMessages();
              this.cancel();
            },
            text: 'Cancel',
          },
        ]);
      }
    } catch (e) {
      await MessageCenter.addErrorFromCode('Hydro_60', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.cancel();
          },
          text: 'Cancel',
        },
      ]);
    } finally {
      this.jobExecuting = false;
      this.enableSketchControls();
    }

    return result;
  }


  private checkInputDemUnit(aoiGeometry: __esri.Geometry) {
    if (aoiGeometry && this.in_dem_elevation_units === "various" && this.in_dem_metadata_url) {
      this.in_dem_elevation_units = "meters";

      const featureLayer = new FeatureLayer({ url: this.in_dem_metadata_url });
      const query = new Query();
      query.spatialRelationship = 'intersects';
      query.where = '1=1';
      query.outFields = ['*'];
      query.geometry = this.aoiGraphicsLayer.graphics.at(0).geometry;

      featureLayer.queryFeatures(query)
        .then((featureSet) => {
          if (featureSet.features.length > 0) {
            const feat = featureSet.features[0];
            const unit: string = feat.attributes.VertUnit;
            // console.log(`metadata VertUnit: ${unit}`);
            if (unit && (unit.toLowerCase().includes('feet') || unit.toLowerCase().includes('ft'))) {
              this.in_dem_elevation_units = 'feet';
            }
          }
        });
    }
  }

  private addOrReplaceResultLayer(layer: Layer): void {
    if (this.resultsGroupLayer) {
      let oldLayer = this.resultsGroupLayer.findLayerById(layer.id);
      if (oldLayer) {
        const index = this.resultsGroupLayer.layers.indexOf(oldLayer);
        oldLayer = this.resultsGroupLayer.remove(oldLayer);
        oldLayer.destroy();
        this.resultsGroupLayer.add(layer, index);
      } else {
        this.resultsGroupLayer.add(layer);
      }
      // ensure outlets layer is on top
      this.ensureOutletsLayerOnTop();
    }
  }

  private ensureOutletsLayerOnTop(): void {
    const layer = this.resultsGroupLayer.findLayerById('result_outlets');
    if (layer) {
      this.resultsGroupLayer.remove(layer);
      this.resultsGroupLayer.add(layer);
    }
  }

  // Extracted Parts of this.submit() in order to render the dem layer for an uploaded dem file.
  @action.bound async demSubmit(needHandleAoiSketch: boolean | undefined = true) {
    // Either file or elevation missing
    if (!(this.result_dem || this.in_dem_file) || !this.in_dem_elevation_units) {
      return;
    }
    // validate the file type and size
    if (this.in_dem_file && !validateDemFile(this.in_dem_file)) {
      return;
    }

    if (this.jobExecuting) {
      throw new Error('Invalid operation, the object is busy.');
    }

    if (!this.workingSpatialReference) {
      if (this.result_dem_boundary) {
        this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
      } else {
        this.workingSpatialReference = this.mapView.spatialReference.clone();
      }
    }
    this.demFileCellSize = undefined;
    this.jobExecuting = true;
    this.jobMessages = [];

    //cancel any outstanding drawing inputs
    if (needHandleAoiSketch) {
      this.aoiSketch.cancel();
      await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
      this.disableSketchControls();
    }
    try {
      //create the gpSessionId
      if (!this.gpSessionId) {
        this.gpSessionId = await this.api.createGpSession();
      }

      if (!this.gpSessionId) {
        return;
      }

      const in_dem = this.result_dem || this.in_dem_file;

      const waitOptions: __esri.GeoprocessorWaitForJobCompletionOptions = {
        interval: 1000,
        signal: undefined,
        statusCallback: this.statusCallback,
      };
      // dummy call to wake up the GenerateStreamlines GP service
      await this.api.generateStreamlinesDummy(waitOptions);

      const results = await this.api.uploadDem(this.gpSessionId, in_dem!, this.in_dem_elevation_units, waitOptions);
      if (results) {
        if (results.result_dem_layer) {
          this.addOrReplaceResultLayer(results.result_dem_layer);
        }

        if (results.result_dem_boundary) {
          this.result_dem_boundary = results.result_dem_boundary;

          // update working spatialreference to match DEM for all future operations
          this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
          if (this.result_dem_boundary.features.length > 0) {
            const feature = this.result_dem_boundary.features[0];
            if (feature !== undefined) {
              this.demFileCellSize = feature.attributes['CellSize'];
              // console.warn('FILE CELL SIZE:', this.demFileCellSize);
              const linearUnits = feature.attributes['LinearUnits'] as GpLinearUnit;
              if (this.demFileCellSize && linearUnits) {
                this.setCellSizeStreamThreshold(this.demFileCellSize, linearUnits);
              }
            }
          }
          this.saveProjectionText();
        }

        if (results.result_dem_boundary_layer) {
          this.addOrReplaceResultLayer(results.result_dem_boundary_layer);
        }
      }
      await this.zoomToSubjectArea();
      this.demModified = true;
    } catch (e) {
      await DelineateWatershedGpApi.logGpError("Hydro_20",
        `Failed while calling GP service DEMUpload or retrieving its results with error: ${(e as Error).message}`,
        this.gpSessionId);
      await DelineateWatershedGpApi.showStandardNotification("Hydro_20");
    } finally {
      // this.setSelectedDemName(this.in_dem_file!.name);
      this.jobExecuting = false;
      if (needHandleAoiSketch) {
        this.enableSketchControls();
      }
    }
  }

  private resolveProceedWithValues?: (value: boolean) => void;

  proceedWithValues(): Promise<boolean> {
    const currentPromise: Promise<boolean> = new Promise((resolve, reject) => {
      this.resolveProceedWithValues = resolve;
    });

    return currentPromise;
  }

  @action.bound accept(): void {
    if (this.resolveProceedWithValues) {
      //resolve the promise
      this.resolveProceedWithValues(true);

      //discard callback handle
      this.resolveProceedWithValues = undefined;
    }
  }

  @action.bound cancel(): void {
    if (this.resolveProceedWithValues) {
      //resolve the promise
      this.resolveProceedWithValues(false);

      //discard callback handle
      this.resolveProceedWithValues = undefined;
    }
  }

  @action.bound async submit(history) {
    if (this.in_generate_hillshade && this.bestAvailableHillshade?.loaded) {
      this.in_generate_hillshade = false;
      this.toggleHillshadeButton = true;
      // a very simple fix to prevent hillshade from popping up every time you click Next
      // this.bestAvailableHillshade.visible = true;
    }
    if (!((this.in_dem_file || this.result_dem) && this.in_dem_elevation_units)) {
      throw new Error('Invalid operation, the object is not in a valid state.');
    }
    if (this.jobExecuting) {
      throw new Error('Invalid operation, the object is busy.');
    }

    // ensure a default working spatialreference
    if (!this.workingSpatialReference) {
      if (this.result_dem_boundary) {
        this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
      } else {
        this.workingSpatialReference = this.mapView.spatialReference.clone();
      }
    }

    // notify callback that submit operation is about to execute
    this.beforeActivityUpdateCallback({ eventName: 'submit' }, this);

    this.jobExecuting = true;
    this.jobMessages = [];

    //cancel any outstanding drawing inputs
    this.aoiSketch.cancel();
    this.culvertSketch.cancel();
    this.outletSketch.cancel();
    this.watershedSketch.cancel();
    this.flowPathSketch.cancel();

    await sleep(100); //introduced sleep to give sketch.cancel operation time to complete before disabling sketches,
    this.disableSketchControls();

    try {
      //create the gpSessionId
      if (!this.gpSessionId) {
        this.gpSessionId = await this.api.createGpSession();
      }

      if (!this.gpSessionId) {
        // this.hydrologyValidationError = true;
        return;
      }

      const waitOptions: __esri.GeoprocessorWaitForJobCompletionOptions = {
        interval: 1000,
        signal: undefined,
        statusCallback: this.statusCallback,
      };

      const currentStepIndex = this.currentStepIndex;
      this.inputStateSnapshot = '';
      if (this.redoStack.length > 0) {
        console.log('calling invalidate');
        const response = await this.api.invalidateGpSession(this.gpSessionId, currentStepIndex);
        console.log('response in dvm: ', response);
      }
      while (this.redoStack.length > 0) {
        this.redoStack.pop();
      }
      //TODO: refactor logic in chronological order for steps 0, 1, & 2
      // Marker
      if (currentStepIndex === 0) {
        const needToReturn = await this.submitStep0(waitOptions);
        if (needToReturn) {
          this.jobExecuting = false;
          this.enableSketchControls();
          return;
        }
        await this.zoomToSubjectArea();
        this.__currentStepIndex = 1;
        this.handleStepChange();
        // END if (currentStepIndex === 0)
      } else if (this.currentStepIndex === 1) {
        await this.createActivityLog(
          'Define Outlet',
          'Define Outlet',
          'Define outlet for TR20 single watershed',
          'Create'
        );

        await this.zoomToSubjectArea().then(async () => {
          await sleep(10000); // let it redraw
          await this.takeAoiScreenshot();
        });

        await this.submitStep1(waitOptions);
        this.__currentStepIndex = 2;
        this.handleStepChange();
        // END if (this.currentStepIndex === 1)
      } else if (this.currentStepIndex === 2) {

        // turn the computed watershed layer off for the snapshot
        const result_watershed_layer = this.resultsGroupLayer.findLayerById('result_watershed');
        if (result_watershed_layer !== undefined) {
          result_watershed_layer.visible = false;
        }

        await this.zoomToSubjectArea().then(async () => {
          await sleep(10000); // let it redraw
          await this.takeAoiScreenshot();
        });

        await this.submitStep2(waitOptions);
        this.__currentStepIndex = 3;
        this.handleStepChange();
        // END if (this.currentStepIndex === 2)

        //if edit LULC
      } else if (this.currentStepIndex === 3 && !this.landUseTableComplete) {
        await this.createActivityLog('LULC', 'Edit Land Use', 'Land use and condition defined', 'Create');
        await this.submitStep3(waitOptions);
        this.salSelectGraphicsLayer.graphics.removeAll();
        // END if (this.currentStepIndex === 3 && !this.landUseTableComplete)
        // if edit hsg
      } else if (this.currentStepIndex === 3 && this.landUseTableComplete) {
        // then run calculateRcn
        await this.createActivityLog('HSG', 'Select HSG', 'HSG selected for soil polygons', 'Create');

        await this.submitCalculateRcn(waitOptions);
        this.__currentStepIndex = 4;
        this.handleStepChange();
        // END if (this.currentStepIndex === 3 && this.landUseTableComplete)
      }

      // clean the graphics layers
      this.aoiGraphicsLayer.graphics.removeAll();
      this.culvertGraphicsLayer.graphics.removeAll();
      this.outletGraphicsLayer.graphics.removeAll();
      if (this.__currentStepIndex !== 2) {
        this.watershedGraphicsLayer.graphics.removeAll();
        this.flowPathGraphicsLayer.graphics.removeAll();
        this.separatorGraphicsLayer.graphics.removeAll();
        this.flowpathSegmentsGraphicsLayer.graphics.removeAll();
      }

      // update the map extent
      await this.zoomToSubjectArea();

    } catch (e) {
      if (this.__currentStepIndex > 0) {
        this.hydrologyValidationError = true;
        if (this.currentStepIndex === 3 && !this.landUseTableComplete) {
          this.hasSoilError = true;
        }
      }
    } finally {
      this.jobExecuting = false;
      this.createSnapShotNewStep();
      this.enableSketchControls();
    }

    // if successful, notify callback of changes after execution completes
    await this.onActivityUpdateCallback({ eventName: 'submit' }, this);
    // }

    // on next this should apply user prefs for layer visibility
    mapPreferences.applyPreference_LayerVisibility(this.resultsGroupLayer.layers);
  }

  private async submitStep0(waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<boolean> {
    // dummy call to wake up the GenerateStreamlines GP service
    await this.api.generateStreamlinesDummy(waitOptions);
    // if this is a DEM file workflow, check if the AOI size in pixels exceeds the maximum
    if (this.in_dem_service_selection === 'upload') {
      const isAoiTooLarge = await this.checkAoiTooLarge();
      if (isAoiTooLarge) {
        await MessageCenter.addNotificationFromCode("Hydro_50",
          [
            {
              callback: () => {
                MessageCenter.clearMessages();
                this.cancel();
              },
              text: 'Cancel'
            }
          ]
        );
        return true;
      }
    }
    // if DEM & AOI
    this.setSelectedDemName(this.in_dem_title);
    await this.persistWatershedDemName(this.in_dem_title);
    this.setWatershedAcreage(0);
    await this.persistWatershedAcreage(0);
    await this.zoomToSubjectArea().then(async () => {
      await sleep(2000); // let it redraw
      await this.takeAoiScreenshot();
    });

    if (this.hasRedo) {
      if (this.demModified)
        await this.createActivityLog(
          'Define DEM',
          'Define AOI and DEM',
          `DEM defined from ${this.in_dem_service_selection === 'upload' ? 'uploaded file' : 'DEM service'}`,
          'Update'
        );
      if (this.aoiModified) {
        await this.createActivityLog(
          'Define AOI',
          'Define AOI and DEM',
          'AOI defined for watershed area',
          'Update'
        );
        this.aoiModified = false;
      }
    } else {
      await this.createActivityLog(
        'Define DEM',
        'Define AOI and DEM',
        `DEM defined from ${this.in_dem_service_selection === 'upload' ? 'uploaded file' : 'DEM service'}`,
        'Create'
      );
      await this.createActivityLog('Define AOI', 'Define AOI and DEM', 'AOI defined for watershed area', 'Create');
    }
    this.demModified = false;

    let throwException = false;

    if (this.in_dem_service_selection !== 'upload') {
      // select in_dem based on current state, use from results if avaialable, otherwise supply and upload the selected file
      const in_dem = this.result_dem || this.in_dem_file;
      try {
        const demUploadResults = await this.api.uploadDem(this.gpSessionId, in_dem!, this.in_dem_elevation_units, waitOptions);
        if (demUploadResults) {
          if (demUploadResults.result_dem_layer) {
            this.addOrReplaceResultLayer(demUploadResults.result_dem_layer);
          }

          if (demUploadResults.result_dem_boundary) {
            this.result_dem_boundary = demUploadResults.result_dem_boundary;

            // update working spatialreference to match DEM for all future operations
            this.workingSpatialReference = this.result_dem_boundary.spatialReference.clone();
            if (this.result_dem_boundary.features.length > 0) {
              const feature = this.result_dem_boundary.features[0];
              if (feature !== undefined) {
                this.demFileCellSize = feature.attributes['CellSize'];
                // console.warn('FILE CELL SIZE:', this.demFileCellSize);
                const linearUnits = feature.attributes['LinearUnits'] as GpLinearUnit;
                if (this.demFileCellSize && linearUnits) {
                  this.setCellSizeStreamThreshold(this.demFileCellSize, linearUnits);
                }
              }
            }
            this.saveProjectionText();
          }

          if (demUploadResults.result_dem_boundary_layer) {
            this.addOrReplaceResultLayer(demUploadResults.result_dem_boundary_layer);
          }
        } else {
          throwException = true;
        }
      } catch (e) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        await DelineateWatershedGpApi.logGpError("Hydro_20",
          `Failed while calling GP service DEMUpload or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_20");
        throw (e);
      }
      if (throwException) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        throw new Error("Failed to upload DEM.");
      }
    }

    try {
      const in_aoi =
        this.aoiGraphicsLayer.graphics.length === 0
          ? this.result_aoi
          : await gisUtils.graphicsLayerToFeatureSet(this.aoiGraphicsLayer, 'polygon', this.workingSpatialReference!);
      const aoiDemResults = await this.api.generateAoiDem(this.gpSessionId, in_aoi, waitOptions);
      if (aoiDemResults) {
        //save current state to stack before making any changes
        this.undoStack.push(this.exportState());

        if (aoiDemResults.result_clipdem_layer) {
          this.addOrReplaceResultLayer(aoiDemResults.result_clipdem_layer);
        }
        if (aoiDemResults.result_aoi) {
          this.result_aoi = aoiDemResults.result_aoi;
        }
        if (aoiDemResults.result_aoi_layer) {
          // this is to ensure the map zooms to AOI once the layer is added
          aoiDemResults.result_aoi_layer.when(() => {
            //zooming to viewpoint ensures that rotation orientation is reset back to 0
            const viewpoint = new Viewpoint();
            viewpoint.targetGeometry = aoiDemResults.result_aoi_layer!.fullExtent;
            viewpoint.rotation = 0;
            this.mapView.goTo(viewpoint);
          });
          this.addOrReplaceResultLayer(aoiDemResults.result_aoi_layer);
        }
      } else {
        throwException = true;
      }
    } catch (e) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      await DelineateWatershedGpApi.logGpError("Hydro_22",
        `Failed while calling GP service GenerateAOIDEM or retrieving its results with error: ${(e as Error).message}`,
        this.gpSessionId);
      await DelineateWatershedGpApi.showStandardNotification("Hydro_22");
      throw (e);
    }
    if (throwException) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      throw new Error("Failed to clip DEM.");
    }

    if (this.in_generate_depthgrid) {
      try {
        const depthGridResult = await this.api.generateSingleResultMap(
          "GenerateDepthGrid",
          "result_depth_grid",
          "Hydro_80",
          this.gpSessionId,
          undefined,
          undefined,
          waitOptions);
        if (depthGridResult) {
          this.addOrReplaceResultLayer(depthGridResult);
        }
      } catch (e) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        await DelineateWatershedGpApi.logGpError("Hydro_80",
          `Failed while calling GP service GenerateDepthGrid or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_80");
        throw (e);
      }
    }

    if (this.in_generate_hillshade) {
      try {
        const hillshadeResult = await this.api.generateSingleResultMap(
          "GenerateHillshade",
          "result_hillshade",
          "Hydro_59",
          this.gpSessionId,
          this.in_dem_elevation_units,
          undefined,
          waitOptions);
        if (hillshadeResult) {
          this.addOrReplaceResultLayer(hillshadeResult);
        }
      } catch (e) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        await DelineateWatershedGpApi.logGpError("Hydro_59",
          `Failed while calling GP service GenerateHillshade or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_59");
        throw (e);
      }
    }

    if (this.in_generate_contours) {
      try {
        const contoursResult = await this.api.generateSingleResultMap(
          "GenerateContours",
          "result_contours",
          "Hydro_58",
          this.gpSessionId,
          this.in_dem_elevation_units,
          this.in_contour_interval,
          waitOptions);
        if (contoursResult) {
          this.addOrReplaceResultLayer(contoursResult);
        }
      } catch (e) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        await DelineateWatershedGpApi.logGpError("Hydro_58",
          `Failed while calling GP service GenerateContours or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_58");
        throw (e);
      }
    }

    if (this.in_generate_streamlines) {
      try {

        const streamlineResults = await this.api.generateStreamlines(
          this.gpSessionId,
          this.in_generate_streamlines,
          this.in_stream_threshold,
          undefined,
          waitOptions);
        if (streamlineResults) {
          if (streamlineResults.result_excess_stream_count) {
            await MessageCenter.addNotificationFromCode("Hydro_79",
              [
                {
                  callback: () => {
                    MessageCenter.clearMessages();
                    this.cancel();
                  },
                  text: 'OK'
                }
              ]
            );
          } else {
            if (streamlineResults.result_streamlines_layer) {
              this.addOrReplaceResultLayer(streamlineResults.result_streamlines_layer);
            } else {
              // trying to guess empirically whether the stream threshold was set too low or too high
              const thresholdTooLow = (this.currentDemCellSize! / this.in_stream_threshold) > 2;
              if (thresholdTooLow) {
                await MessageCenter.addNotificationFromCode("Hydro_69",
                  [
                    {
                      callback: () => {
                        MessageCenter.clearMessages();
                        this.cancel();
                      },
                      text: 'OK'
                    }
                  ]
                );
              }
            }
          }
        } else {
          throwException = true;
        }
        // throw new Error();
      } catch (e) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        await DelineateWatershedGpApi.logGpError("Hydro_61",
          `Failed while calling GP service GenerateStreamlines or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_61");
        throw (e);
      }
      if (throwException) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        throw new Error("Failed to generate streamlines.");
      }
    }
    return false;
  }

  private async submitStep1(waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<void> {
    let throwException = false;
    let didSaveState = false;
    try {
      let in_culverts: __esri.FeatureSet | undefined = await gisUtils.graphicsLayerToFeatureSet(
        this.culvertGraphicsLayer, "polyline", this.workingSpatialReference!);
      const hasCulverts = (in_culverts.features.length > 0);
      if (!hasCulverts) {
        in_culverts = undefined;
      }

      if (hasCulverts || this.previouslyHadCulverts || (!this.in_generate_streamlines)) {
        const streamlineResults = await this.api.generateStreamlines(
          this.gpSessionId, this.in_generate_streamlines, this.in_stream_threshold, in_culverts, waitOptions);

        if (streamlineResults) {
          //save current state to stack before making any changes
          this.undoStack.push(this.exportState());
          didSaveState = true;

          if (this.in_generate_streamlines) {
            if (streamlineResults.result_excess_stream_count) {
              await MessageCenter.addNotificationFromCode("Hydro_79",
                [
                  {
                    callback: () => {
                      MessageCenter.clearMessages();
                      this.cancel();
                    },
                    text: 'OK'
                  }
                ]
              );
            } else {
              if (streamlineResults.result_streamlines_layer) {
                this.addOrReplaceResultLayer(streamlineResults.result_streamlines_layer);
              } else {
                // trying to guess empirically whether the stream threshold was set too low or too high
                const thresholdTooLow = (this.currentDemCellSize! / this.in_stream_threshold) > 2;
                if (thresholdTooLow) {
                  await MessageCenter.addNotificationFromCode("Hydro_69",
                    [
                      {
                        callback: () => {
                          MessageCenter.clearMessages();
                          this.cancel();
                        },
                        text: 'OK'
                      }
                    ]
                  );
                }
              }
            }
          }

          if (hasCulverts && streamlineResults.result_culverts) {
            this.result_culverts = streamlineResults.result_culverts;
          }

          if (hasCulverts && streamlineResults.result_culverts_layer) {
            this.addOrReplaceResultLayer(streamlineResults.result_culverts_layer);
          }
        } else {
          throwException = true;
        }
      }
      this.previouslyHadCulverts = hasCulverts;
    } catch (e) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      await DelineateWatershedGpApi.logGpError("Hydro_61",
        `Failed while calling GP service GenerateStreamlines or retrieving its results with error: ${(e as Error).message}`,
        this.gpSessionId);
      await DelineateWatershedGpApi.showStandardNotification("Hydro_61");
      throw (e);
    }
    if (throwException) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      throw new Error("Failed to generate streamlines.");
    }

    try {
      const in_outlets = await gisUtils.graphicsLayerToFeatureSet(
        this.outletGraphicsLayer, "polyline", this.workingSpatialReference!);

      const watershedResult = await this.api.generateWatershed(
        this.gpSessionId, in_outlets, this.in_dem_elevation_units, waitOptions);

      if (watershedResult) {
        if (!didSaveState) {
          //save current state to stack before making any changes
          this.undoStack.push(this.exportState());
          didSaveState = true;
        }
        if (watershedResult.result_watershed) {
          this.result_watershed = watershedResult.result_watershed;
          await this.addWatershedGraphics(this.result_watershed, true);
          this.persistWatershedInformation(watershedResult.result_watershed.features[0]);
        }
        if (watershedResult.result_flowpath) {
          this.result_flowpath = watershedResult.result_flowpath;
          await this.addFlowPathGraphics(this.result_flowpath);
        }
        if (watershedResult.result_outlets) {
          this.result_outlets = watershedResult.result_outlets;
          this.outletsFeature = watershedResult.result_outlets;
        }
        if (watershedResult.result_outlets_layer) {
          this.addOrReplaceResultLayer(watershedResult.result_outlets_layer);
        }
        if (watershedResult.result_watershed_layer) {
          this.addOrReplaceResultLayer(watershedResult.result_watershed_layer);
        }
        if (watershedResult.result_flowpath_layer) {
          this.addOrReplaceResultLayer(watershedResult.result_flowpath_layer);
        }
      } else {
        throwException = true;
      }
    } catch (e) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      await DelineateWatershedGpApi.logGpError("Hydro_88",
        `Failed while calling GP service GenerateWatershed or retrieving its results with error: ${(e as Error).message}`,
        this.gpSessionId);
      await DelineateWatershedGpApi.showStandardNotification("Hydro_88");
      throw (e);
    }
    if (throwException) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      throw new Error("Failed to generate watershed.");
    }
  }

  private async submitStep2(waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<void> {
    let throwException = false;
    if (this.watershedModified || this.flowPathModified) {
      const watershedModified = this.watershedModified;
      const flowPathModified = this.flowPathModified;
      try {
        if (!this.watershedGraphicsLayer) {
          throw new Error('Invalid operation; edited watershed not available.');
        }
        if (!this.flowPathGraphicsLayer) {
          throw new Error('Invalid operation; edited flowPath not available.');
        }
        const in_upd_watershed = await gisUtils.graphicsLayerToFeatureSet(
          this.watershedGraphicsLayer,
          'polygon',
          this.workingSpatialReference!
        );
        const in_upd_flowPath = await gisUtils.graphicsLayerToFeatureSet(
          this.flowPathGraphicsLayer,
          'polyline',
          this.workingSpatialReference!
        );

        const watershedResult = await this.api.updateWatershed(
          this.gpSessionId, in_upd_watershed, in_upd_flowPath, this.watershedModified, this.flowPathModified, this.in_dem_elevation_units, waitOptions);

        if (watershedResult) {
          if (watershedResult.result_watershed) {
            this.result_watershed = watershedResult.result_watershed;
            await this.addWatershedGraphics(this.result_watershed);
            this.persistWatershedInformation(watershedResult.result_watershed.features[0]);
            this.watershedModified = false;
            this.flowPathModified = false;
          }
          if (watershedResult.result_watershed_layer) {
            this.addOrReplaceResultLayer(watershedResult.result_watershed_layer);
          }
          if (watershedResult.result_flowpath_orig_layer) {
            this.addOrReplaceResultLayer(watershedResult.result_flowpath_orig_layer);
          } else {
            let oldLayer = this.resultsGroupLayer.findLayerById('result_flowpath_orig');
            if (oldLayer) {
              oldLayer = this.resultsGroupLayer.remove(oldLayer);
              oldLayer.destroy();
            }
          }
          if (watershedResult.result_flowpath_layer) {
            this.addOrReplaceResultLayer(watershedResult.result_flowpath_layer);
          }

          // check if the updated watershed falls within the TR20 parameters
          if (watershedResult.result_watershed &&
            watershedResult.result_watershed.features &&
            (watershedResult.result_watershed.features.length > 0) &&
            watershedResult.result_watershed.features[0].attributes) {
            const watershedAttributes = watershedResult.result_watershed.features[0].attributes;
            const flowLength = watershedAttributes.MaxFlowLengthFt;
            const slope = watershedAttributes.Avg_Slope;
            const area = watershedAttributes.Acres;
            if ((!flowLength) || (!(200 <= flowLength && flowLength <= 26000))) {
              await MessageCenter.addErrorFromCode('Hydro_14');
              throwException = true;
            } else if ((!slope) || (!(0.5 <= slope && slope <= 64))) {
              await MessageCenter.addErrorFromCode('Hydro_49');
              throwException = true;
            } else if ((!area) || (area < 0.096)) {
              await MessageCenter.addErrorFromCode('Hydro_68');
              throwException = true;
            } else if (area > 2000) {
              await MessageCenter.addWarningFromCode('Hydro_13', [
                {
                  callback: () => {
                    MessageCenter.clearMessages();
                    this.cancel();
                  },
                  text: 'OK',
                },
              ]);
            }
          } else {
            throwException = true;
          }

          await this.zoomToSubjectArea().then(async () => {
            await sleep(10000); // let it redraw
            await this.takeAoiScreenshot();
          });
        } else {
          throwException = true;
        }
      } catch (e) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        await DelineateWatershedGpApi.logGpError("Hydro_81",
          `Failed while calling GP service UpdateWatershed or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_81");
        throw (e);
      }
      if (throwException) {
        await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
        throw new Error("Failed to update watershed.");
      }
      let comment = 'Flow path and watershed outline have';
      if (!flowPathModified) {
        comment = 'Watershed outline has';
      } else if (!watershedModified) {
        comment = 'Flow path has';
      }
      try {
        await this.createActivityLog(
          'Edit Watershed',
          'UpdateWatershed',
          `${comment} been edited`,
          'Update',
          undefined,
          'System Generated'
        );
      } catch {
        // pass
      }

      if (flowPathModified) {
        try {
          const postJSON: ProjectCommentRequest = {
            commentAuthor: 'System.Generated@',
            commentTypeId: 2,
            editStatus: 'add',
            projectComment: 'Flow path for this watershed has been edited.',
            workflowStep: 'Edit Watershed',
            projectUuid: efh2Store.projectsStore.currentProject.projectUuid,
          };
          await PRJAPI.ProjectCommentController.createProjectCommentByProjectUuidUsingPOST(postJSON);
        } catch (e) {
          console.error(e);
          MessageCenter.addInfoFromCode('1_APP_TR20_Hydro_107');
        }
      }
    }
    if (efh2Store.tocManuallyUpdated) {
      try {
        const postJSON: ProjectCommentRequest = {
          commentAuthor: 'System.Generated@',
          commentTypeId: 2,
          editStatus: 'add',
          projectComment: efh2Store.tocManuallyUpdatedComment,
          workflowStep: 'Edit Watershed',
          projectUuid: efh2Store.projectsStore.currentProject.projectUuid,
        };
        await PRJAPI.ProjectCommentController.createProjectCommentByProjectUuidUsingPOST(postJSON);
        await this.createActivityLog(
          'Edit Watershed',
          'UpdateWatershed',
          'Time of Concentration has been edited',
          'Update',
          undefined,
          'System Generated'
        );
      } catch (e) {
        console.error(e);
        MessageCenter.addInfoFromCode('1_APP_TR20_Hydro_107');
      } finally {
        efh2Store.tocManuallyUpdated = false
      }
    }
    try {
      if (!this.result_watershed) {
        throw new Error("Watershed is not defined.");
      }

      const lulcSelection = await this.api.fetchLULCFeatureSet(this.result_watershed);

      const lulcResult = await this.api.generateSingleFeatureResult(
        "PrepareLULC", "in_landuse", "result_landuse", "Hydro_85", false,
        this.gpSessionId, lulcSelection, undefined, undefined, waitOptions);

      if (lulcResult) {
        //save current state to stack before making any changes
        this.undoStack.push(this.exportState());
        this.selectedLandUse.splice(0, this.selectedLandUse.length);
        this.selectedSoils.splice(0, this.selectedSoils.length);
        this.salSelectGraphicsLayer.graphics.removeAll();

        this.output_SAL_LANDUSE = lulcResult.result_feature_set;

        const edit_SAL_LANDUSE_featureSet = gisUtils.cloneFeatureSet(this.output_SAL_LANDUSE!);
        this.edit_SAL_LANDUSE_graphicsLayer = gisUtils.featureSetToGraphicsLayer(
          edit_SAL_LANDUSE_featureSet, __editLandUseSymbol);
        this.edit_SAL_LANDUSE_graphicsLayer.title = 'Land Use';
        this.edit_SAL_LANDUSE_graphicsLayer.id = 'salLandGraphics';

        //todo: cleanup event handlers
        this.edit_SAL_LANDUSE_graphicsLayer.on('layerview-create', (e) => {
          this.edit_SAL_LANDUSE_graphicsLayerView = e.layerView as GraphicsLayerView;
          this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
        });
        this.edit_SAL_LANDUSE_graphicsLayer.on('layerview-destroy', (e) => {
          this.edit_SAL_LANDUSE_graphicsLayerView = undefined;
          this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
        });
        this.addOrReplaceResultLayer(this.edit_SAL_LANDUSE_graphicsLayer);
      } else {
        throwException = true;
      }
    } catch (e) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);

      if ((e as any).message.toLowerCase().includes("unable to complete operation") || ((e as any).code === 400) || (e as any).message.toLowerCase().includes("failed to fetch") || (e as any).message.toLowerCase().includes("mapserver not started")) {
        await DelineateWatershedGpApi.logGpError("Hydro_91",
          `Failed while calling GP service PrepareLULC or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_91");
      } else {
        await DelineateWatershedGpApi.logGpError("Hydro_85",
          `Failed while calling GP service PrepareLULC or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_85");
      }

      throw (e);
    }
    if (throwException) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      throw new Error("Failed to prepare LULC.");
    }
  }

  private logUndoStack(): void {
    let index = 0;
    while (true) {
      if (index >= this.undoStack.length) return;
      console.warn("************");
      console.warn(`UNDO ELEMENT ${index}`);
      const state = this.undoStack[index] as IUndoRedoState;
      for (const layer of state.resultsGroupLayerItems) {
        console.warn(`${layer.id}::${layer.title}`);
      }
      console.warn("************");
      index++;
    }
  }

  private async submitStep3(waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<void> {
    let throwException = false;
    try {
      if (!this.result_watershed || !this.output_SAL_LANDUSE) {
        throw new Error("Invalid operation.");
      }

      const soilSelection = await this.api.fetchSoilFeatureSet(this.result_watershed);
      if (soilSelection === undefined) {
        throw new Error("Current watershed is not entirely covered with soil polygons.");
      }

      const inLandUses = gisUtils.cloneFeatureSet(this.output_SAL_LANDUSE);
      inLandUses.features = this.edit_SAL_LANDUSE_graphicsLayer!.graphics.toArray();
      // Make sure the Object IDs of the graphic features submitted are unique
      DelineateWatershedInputStateVM.ensureUniqueOids(inLandUses.features);

      const soilResult = await this.api.generateSingleFeatureResult(
        "PrepareSoils", "in_soils", "result_soils", "Hydro_86", true,
        this.gpSessionId, soilSelection, "in_lulc", inLandUses, waitOptions);

      if (soilResult) {
        if (soilResult.result_feature_set) {
          this.result_soils = soilResult.result_feature_set;
        }

        if (soilResult.result_feature_layer) {
          this.result_soils_layer = soilResult.result_feature_layer;
          this.addOrReplaceResultLayer(this.result_soils_layer);
        }

        if (soilResult.result_feature_set) {
          this.salSoilGraphicsLayer.removeAll();
          soilResult.result_feature_set.features.forEach((g) => {
            const graphic = g.clone();
            graphic.symbol = salSoilSymbol;
            this.salSoilGraphicsLayer.add(graphic);
          });
          this.salSoilArray = JSON.parse(JSON.stringify(soilResult.result_feature_set.features));
        }
      } else {
        throwException = true;
      }
    } catch (e) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      if ((e as any).message.toLowerCase().includes("unable to complete operation") || ((e as any).code === 400) || (e as any).message.toLowerCase().includes("failed to fetch") || (e as any).message.toLowerCase().includes("mapserver not started")) {
        await DelineateWatershedGpApi.logGpError("Hydro_92",
          `Failed while calling GP service PrepareSoils or retrieving its results with error: ${(e as any).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_92");
      } else {
        await DelineateWatershedGpApi.logGpError("Hydro_86",
          `Failed while calling GP service PrepareSoils or retrieving its results with error: ${(e as Error).message}`,
          this.gpSessionId);
        await DelineateWatershedGpApi.showStandardNotification("Hydro_86");
      }
      throw (e);
    }
    if (throwException) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      throw new Error("Failed to prepare soils.");
    }
  }

  public deleteSoilData(): void {
    this.result_soils = undefined;
    const layer = this.resultsGroupLayer.findLayerById("result_soils");
    if (layer) {
      this.resultsGroupLayer.remove(layer);
      layer.destroy();
    }
    if (this.result_soils_layer) {
      this.result_soils_layer.destroy();
      this.result_soils_layer = undefined;
    }
    if (this.salSoilGraphicsLayer) {
      this.salSoilGraphicsLayer.removeAll();
    }
    this.salSoilArray = [];
  }

  private hideResultWatershedLayer(): void {
    const result_watershed_layer = this.resultsGroupLayer.findLayerById('result_watershed');
    const output_SAL_WATERSHED_layer = this.resultsGroupLayer.findLayerById('output_SAL_WATERSHED');
    const output_RCN_WATERSHED_layer = this.resultsGroupLayer.findLayerById('output_RCN_WATERSHED');
    if (result_watershed_layer !== undefined) {
      result_watershed_layer.visible =
        output_SAL_WATERSHED_layer === undefined && output_RCN_WATERSHED_layer === undefined;
    }
    if (output_SAL_WATERSHED_layer !== undefined) {
      output_SAL_WATERSHED_layer.visible = output_RCN_WATERSHED_layer === undefined;
    }
  }

  private async submitCalculateRcn(waitOptions?: __esri.GeoprocessorWaitForJobCompletionOptions): Promise<void> {
    let throwException = false;
    let showMessage = true;
    try {
      if (this.currentStepIndex !== 3) {
        throw new Error('Invalid operation.');
      }
      if (!this.result_watershed || !this.result_soils) {
        throw new Error('Invalid operation.');
      }

      const inSoils = this.result_soils;
      for (let i = 0; i < inSoils.features.length; i++) {
        inSoils.features[i].attributes.HYDGROUP = this.salSoilArray[i].attributes.HYDGROUP;
      }

      const rcnResult = await this.api.calculateRcn(this.gpSessionId, inSoils, waitOptions);

      if (rcnResult) {
        // Weighted Curve Number validation
        if (rcnResult.result_rcn_watershed) {
          const runoffCN = rcnResult.result_rcn_watershed.features[0].attributes.RCN;
          if ((runoffCN < 30) || (runoffCN > 100)) {
            await MessageCenter.addErrorFromCode('Hydro_48', [
              {
                callback: () => {
                  MessageCenter.clearMessages();
                  this.cancel();
                },
                text: 'Cancel',
                cancelSharesCallback: true,
              },
            ]);
            const dialogResult: boolean = await this.proceedWithValues();
            if (dialogResult !== true) {
              showMessage = false;
              throw new Error(`RCN equals to ${runoffCN} which is out of range (30 to 100).`);
            }
          }
          // compute and validate time of concentration
          const wstc = DelineateWatershedInputStateVM.calcTimeOfConcentration(rcnResult.result_rcn_watershed.features[0]);
          if ((wstc < 0.1) || (wstc > 10)) {
            if (wstc < 0.1) {
              await MessageCenter.addNotificationFromCode('Hydro_108');
            } else {
              await MessageCenter.addErrorFromCode('Hydro_15', [
                {
                  callback: () => {
                    MessageCenter.clearMessages();
                    this.cancel();
                  },
                  text: 'Cancel',
                  cancelSharesCallback: true,
                },
              ]);
              const dialogResult: boolean = await this.proceedWithValues();
              if (dialogResult !== true) {
                showMessage = false;
                throw new Error(`Time of concentration equals to ${wstc} which is out of range (0.1 to 10).`);
              }
            }
          }
        }

        //invalidate and clear the redo stack
        this.inputStateSnapshot = '';
        while (this.redoStack.length > 0) {
          this.redoStack.pop();
        }
        //save current state to stack before making any changes
        this.undoStack.push(this.exportState());

        // clean the graphics layers
        this.aoiGraphicsLayer.graphics.removeAll();
        this.culvertGraphicsLayer.graphics.removeAll();
        this.outletGraphicsLayer.graphics.removeAll();
        this.watershedGraphicsLayer.graphics.removeAll();
        this.flowPathGraphicsLayer.graphics.removeAll();
        this.separatorGraphicsLayer.graphics.removeAll();
        this.flowpathSegmentsGraphicsLayer.graphics.removeAll();

        // update the model state
        if (rcnResult.result_rcn_watershed) {
          this.output_RCN_WATERSHED = rcnResult.result_rcn_watershed;
        }
        if (rcnResult.result_rcn_subareas) {
          this.output_RCN_SUBAREAS = rcnResult.result_rcn_subareas;
          this.rcnSubareasArray = JSON.parse(JSON.stringify(rcnResult.result_rcn_subareas.features));
        }

        if (rcnResult.result_rcn_subareas_layer) {
          this.resultsGroupLayer.add(rcnResult.result_rcn_subareas_layer);
        }
        if (rcnResult.result_rcn_watershed_layer) {
          this.resultsGroupLayer.add(rcnResult.result_rcn_watershed_layer);
        }

        // ensure outlets layer is on top
        this.ensureOutletsLayerOnTop();

        if (rcnResult.result_rcn_watershed) {
          this.rcnWatershedGraphicsLayer.removeAll();
          rcnResult.result_rcn_watershed.features.forEach((g) => {
            const graphic = g.clone();
            graphic.symbol = rcnWatershedSymbol;
            this.rcnWatershedGraphicsLayer.add(graphic);
          });
          this.rcnWatershedArray = JSON.parse(JSON.stringify(rcnResult.result_rcn_watershed.features));
        }
      } else {
        throwException = true;
      }
    } catch (e) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      await DelineateWatershedGpApi.logGpError("Hydro_87",
        `Failed while calling GP service CalculateRCN or retrieving its results with error: ${(e as Error).message}`,
        this.gpSessionId);
      if (showMessage) {
        await DelineateWatershedGpApi.showStandardNotification("Hydro_87");
      }
      throw (e);
    }
    if (throwException) {
      await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex);
      throw new Error("Failed to calculate RCN.");
    }

    // turn the computed watershed layer off if showing the modified one
    this.hideResultWatershedLayer();
  }

  private static calcTimeOfConcentration(watershed: Graphic): number {
    const runoffCN = watershed.attributes.RCN;
    const watershedLen = watershed.attributes.MaxFlowLengthFt;
    const watershedSlope = watershed.attributes.Avg_Slope;
    const hlf = Math.pow(watershedLen, 0.80);
    const s = (1000.0 / runoffCN) - 9;
    const sf = Math.pow(s, 0.70);
    const slpf = 1140 * Math.pow(watershedSlope, 0.50);
    const wstc = hlf * sf / slpf;
    return wstc;
  }

  private static validateRcnWatershedParams(watershed: Graphic): { outOfRange: boolean, message: string } {
    const runoffCN = watershed.attributes.RCN;
    if ((runoffCN < 30) || (runoffCN > 100)) {
      return { outOfRange: true, message: `RCN equals to ${runoffCN} which is out of range (30 to 100).` };
    }
    const wstc = DelineateWatershedInputStateVM.calcTimeOfConcentration(watershed);
    if ((wstc < 0.1) || (wstc > 10)) {
      if (wstc < 0.1) {
        return { outOfRange: false, message: "" };
      } else {
        return { outOfRange: true, message: `Time of concentration equals to ${wstc} which is out of range (0.1 to 10).` };
      }
    }
    return { outOfRange: false, message: "" };
  }

  private static ensureUniqueOids(features: Graphic[], oidField: string | undefined = 'OBJECTID'): boolean {
    let result: boolean = false;

    let maxOid: number = 0;
    for (const oid of features.map((f) => f.attributes[oidField] as number)) {
      if (oid > maxOid) {
        maxOid = oid;
      }
    }

    const oidsSeen: number[] = [];
    for (const f of features) {
      if (oidsSeen.some((oid) => oid === (f.attributes[oidField] as number))) {
        maxOid++;
        f.attributes[oidField] = maxOid;
        result = true;
      } else {
        oidsSeen.push(f.attributes[oidField] as number);
      }
    }
    return result;
  }

  private readonly undoStack: any[] = [];
  private readonly redoStack: any[] = [];
  private inputStateSnapshot: string = '';
  private inputLandUseSnapshot: string = '';
  private inputStateSnapshotNewStep: string = '';
  private inputLandUseSnapshotNewStep: string = '';

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  @computed get hasRedo(): boolean {
    return this.redoStack.length > 0;
  }

  emptyRedoStack() {
    while (this.redoStack.length > 0) {
      this.redoStack.pop();
    }
  }

  async invalidateGpSession(fullDelete: boolean = false): Promise<void> {
    await this.api.invalidateGpSession(this.gpSessionId, this.currentStepIndex, fullDelete);
  }

  get canRedo(): boolean {
    if (this.redoStack.length === 0) {
      return false;
    }
    if (!this.inputStateSnapshot) {
      return false;
    }

    const snapshot = this.exportInputStateSnapshot();

    if (snapshot === this.inputStateSnapshot) {
      //redo stack is still valid
      return true;
    } else {
      //redo stack is no longer valid because something has changed
      return false;
    }
  }

  get canRedoLandUse(): boolean {
    if (!this.inputLandUseSnapshot) {
      return false;
    }

    const snapshot = this.exportInputLandUseSnapshot();

    if (snapshot === this.inputLandUseSnapshot) {
      //redo stack is still valid
      return true;
    } else {
      //redo stack is no longer valid because something has changed
      return false;
    }
  }

  @action.bound async undo() {
    if (!this.canUndo) {
      throw new Error('Invalid operation.');
    }
    this.hydrologyValidationError = false;

    if (this.currentStepIndex === 1) {
      this.deactivateSketchTools(this.outletSketch);
      this.deactivateSketchTools(this.culvertSketch);
    }
    if (this.currentStepIndex === 2) {
      this.deactivateSketchTools(this.watershedSketch);
      this.deactivateSketchTools(this.flowPathSketch);
      this.canResetSmartCut = false;
    }

    if (this.currentStepIndex === 3 && this.result_watershed) {
      const g = this.result_watershed.features[0].clone();
      if (g.geometry.spatialReference.wkid !== this.mapView.spatialReference.wkid) {
        g.geometry = esriProjection.project(g.geometry, this.mapView.spatialReference) as Geometry;
        if (!g.geometry) {
          throw new Error('Unexpected result.');
        }
      }
      g.symbol = validWatershedSymbol;

      this.originalWatershedGraphic = g.clone();

      if (this.result_flowpath) {
        this.result_flowpath.features.forEach((graphic) => {

          const g = graphic.clone();
          if (g.geometry.spatialReference.wkid !== this.mapView.spatialReference.wkid) {
            g.geometry = esriProjection.project(g.geometry, this.mapView.spatialReference) as Geometry;
            if (!g.geometry) {
              throw new Error('Unexpected result.');
            }
          }
          this.updateFlowPathProperties(g);
        });
      }
    }

    this.beforeActivityUpdateCallback({ eventName: 'undo' }, this);

    // push current state into REDO stack before applying UNDO
    this.redoStack.push(this.exportState());
    // apply the UNDO operation
    // console.warn(`BEFORE UNDO: ${this.undoStack.length}`);
    this.importState(this.undoStack.pop());
    // console.warn(`AFTER UNDO: ${this.undoStack.length}`);
    // create a snapshot for REDO validation
    this.inputStateSnapshot = this.exportInputStateSnapshot();
    this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
    this.__currentStepIndex--;
    this.handleStepChange();
    await this.zoomToSubjectArea();
    // efh2Store.aoiContainsWatershed = true;

    await this.onActivityUpdateCallback({ eventName: 'undo' }, this);
  }

  //this method, should reapply something that got undone
  @action.bound async redo() {
    if (!this.canRedo) {
      throw new Error('Invalid operation.');
    }

    this.beforeActivityUpdateCallback({ eventName: 'redo' }, this);

    // push current state into UNDO stack before applying REDO
    this.undoStack.push(this.exportState());
    // console.warn(`AFTER REDO: ${this.undoStack.length}`);
    // apply the REDO operation
    this.importState(this.redoStack.pop());
    // create a snapshot for REDO validation
    this.inputStateSnapshot = this.exportInputStateSnapshot();
    this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
    this.__currentStepIndex++;
    this.handleStepChange();
    await this.zoomToSubjectArea();

    await this.onActivityUpdateCallback({ eventName: 'redo' }, this);
  }

  get changesMade() {
    if (this.currentStepIndex === 3 && !this.landUseTableComplete) {
      const snapLU = this.exportInputLandUseSnapshot();
      return !(snapLU === this.inputLandUseSnapshotNewStep);
    }
    else {
      const snap = this.exportInputStateSnapshot();
      return !(snap === this.inputStateSnapshotNewStep);
    }
  }

  @action.bound createSnapShot() {
    this.inputStateSnapshot = this.exportInputStateSnapshot();
    this.inputLandUseSnapshot = this.exportInputLandUseSnapshot();
  }

  @action.bound createSnapShotNewStep() {
    this.inputStateSnapshotNewStep = this.exportInputStateSnapshot();
    this.inputLandUseSnapshotNewStep = this.exportInputLandUseSnapshot();
  }

  private exportInputStateSnapshot(): string {
    const state: IInputState = {
      expressMode: this.expressMode,
      in_dem_service_selection: this.in_dem_service_selection,
      in_dem_file_name: this.in_dem_file?.name,
      in_dem_elevation_units: this.in_dem_elevation_units,
      in_dem_title: this.demSelectName,
      in_generate_contours: this.in_generate_contours,
      in_contour_interval: this.in_contour_interval,
      in_generate_hillshade: this.in_generate_hillshade,
      in_generate_depthgrid: this.in_generate_depthgrid,
      in_generate_streamlines: this.in_generate_streamlines,
      in_stream_threshold: this.in_stream_threshold,
      salSoilArray: this.salSoilArray,
      aoiGraphics: this.aoiGraphicsLayer.graphics.toArray(),
      culvertGraphics: this.culvertGraphicsLayer.graphics.toArray(),
      outletGraphics: this.outletGraphicsLayer.graphics.toArray(),
      watershedGraphics: this.watershedGraphicsLayer.graphics.toArray(),
      flowPathGraphics: this.flowPathGraphicsLayer.graphics.toArray(),
      separatorGraphics: this.separatorGraphicsLayer.graphics.toArray(),
      flowpathSegmentsGraphics: this.flowpathSegmentsGraphicsLayer.graphics.toArray()
    };

    const snapshot = JSON.stringify(state);
    return snapshot;
  }

  private exportInputLandUseSnapshot(): string {
    const state = {
      salLandGraphics: this.edit_SAL_LANDUSE_graphicsLayerView
        ? (this.edit_SAL_LANDUSE_graphicsLayerView.layer as GraphicsLayer).graphics.toArray()
        : [],
    };
    const snapshot = JSON.stringify(state);
    return snapshot;
  }

  private cloneGraphics(graphicsArray: Graphic[], symbol: Symbol, targetSpatialReference: SpatialReference): Graphic[] {
    const results = [] as Graphic[];
    graphicsArray.forEach((graphic) => {
      const g = graphic.clone();
      g.symbol = symbol;
      g.geometry = esriProjection.project(g.geometry, targetSpatialReference) as Geometry;
      results.push(g);
    });
    return results;
  }
  private exportStateAtStep(step: number): IUndoRedoState | undefined {
    if (
      step === 4 &&
      this.result_dem &&
      this.result_aoi &&
      this.result_outlets &&
      this.result_watershed &&
      this.output_SAL_LANDUSE &&
      this.result_soils &&
      // this.output_SAL_WATERSHED &&
      this.output_RCN_WATERSHED
    ) {
      const units: string = this.result_dem_boundary?.features[0].attributes['ElevationUnits'];
      const projectionText: string = this.result_dem_boundary?.features[0].attributes.SpatialReference;
      if (projectionText) {
        this.projectionText = projectionText.replaceAll('_', ' ');
      }
      this.updateProjectionText();
      const state: IUndoRedoState = {
        expressMode: false,
        in_dem_file: undefined,
        in_dem_elevation_units: units,
        in_generate_contours: this.result_contours ? true : false,
        in_contour_interval: 2, //this.in_contour_interval,
        in_generate_hillshade: this.result_hillshade ? true : false,
        in_generate_depthgrid: this.result_depthgrid ? true : false,
        in_generate_streamlines: this.in_generate_streamlines,
        in_stream_threshold: 2, //this.in_stream_threshold,

        // TODO: this is not a perfect reconstruction, it is state after prepareSAL, but lacks users changes
        // TODO: fix backend RCN procedure to echo user's inputs
        // salLandArray: this.salLandArray,
        salSoilArray: this.salSoilArray,
        rcnWatershedArray: [],
        rcnSubareasArray: [],

        aoiGraphics: [],
        culvertGraphics: [],
        outletGraphics: [],
        watershedGraphics: [],
        flowPathGraphics: [],
        separatorGraphics: [],
        flowpathSegmentsGraphics: [],
        salLandGraphics: [],
        salSoilGraphics: [],
        rcnWatershedGraphics: [],

        result_dem: this.result_dem,
        result_dem_boundary: this.result_dem_boundary,
        result_aoi: this.result_aoi,
        result_culverts: this.result_culverts,
        result_clipdem: this.result_clipdem,
        result_hillshade: this.result_hillshade,
        result_contours: this.result_contours,
        result_depthgrid: this.result_depthgrid,
        result_streamlines: this.result_streamlines,
        result_outlets: this.result_outlets,
        result_watershed: this.result_watershed,
        result_flowpath: this.result_flowpath,

        output_SAL_WATERSHED: this.output_SAL_WATERSHED,
        result_soils: this.result_soils,
        output_SAL_LANDUSE: this.output_SAL_LANDUSE,
        output_RCN_WATERSHED: this.output_RCN_WATERSHED,
        output_RCN_SUBAREAS: this.output_RCN_SUBAREAS,

        resultsGroupLayerItems: [],
      };
      ___resultNamesByStep.forEach((result) => {
        if (result.step <= step) {
          const layer = this.resultsGroupLayer.findLayerById(result.result);
          if (layer) state.resultsGroupLayerItems.push(layer);
        }
      });
      return state;
    } //step 3

    if (
      step === 3 &&
      this.result_dem &&
      this.result_aoi &&
      this.result_outlets &&
      this.result_watershed &&
      this.output_SAL_LANDUSE
    ) {
      const salLandGraphics = this.edit_SAL_LANDUSE_graphicsLayer
        ? this.cloneGraphics(
          this.edit_SAL_LANDUSE_graphicsLayer.graphics.toArray(),
          __editLandUseSymbol,
          this.mapView.spatialReference
        )
        : [];

      const salSoilGraphics = this.result_soils
        ? this.cloneGraphics(
          this.result_soils.features,
          salSoilSymbol,
          this.mapView.spatialReference
        )
        : [];
      const units: string = this.result_dem_boundary?.features[0].attributes['ElevationUnits'];
      const state: IUndoRedoState = {
        expressMode: false,
        in_dem_file: undefined,
        in_dem_elevation_units: units,
        in_generate_contours: this.result_contours ? true : false,
        in_contour_interval: 2, //this.in_contour_interval,
        in_generate_hillshade: this.result_hillshade ? true : false,
        in_generate_depthgrid: this.result_depthgrid ? true : false,
        in_generate_streamlines: this.in_generate_streamlines,
        in_stream_threshold: 2, //this.in_stream_threshold,

        // salLandArray: [],
        salSoilArray: [],
        rcnWatershedArray: [],
        rcnSubareasArray: [],

        aoiGraphics: [],
        culvertGraphics: [],
        outletGraphics: [],
        watershedGraphics: [],
        flowPathGraphics: [],
        separatorGraphics: [],
        flowpathSegmentsGraphics: [],
        salLandGraphics: salLandGraphics,
        salSoilGraphics: salSoilGraphics,
        rcnWatershedGraphics: [],

        result_dem: this.result_dem,
        result_dem_boundary: this.result_dem_boundary,
        result_aoi: this.result_aoi,
        result_culverts: this.result_culverts,
        result_clipdem: this.result_clipdem,
        result_hillshade: this.result_hillshade,
        result_contours: this.result_contours,
        result_depthgrid: this.result_depthgrid,
        result_streamlines: this.result_streamlines,
        result_outlets: this.result_outlets,
        result_watershed: this.result_watershed,
        result_flowpath: this.result_flowpath,

        output_SAL_WATERSHED: undefined,
        result_soils: this.result_soils,
        output_SAL_LANDUSE: this.output_SAL_LANDUSE,
        output_RCN_WATERSHED: undefined,
        output_RCN_SUBAREAS: undefined,

        resultsGroupLayerItems: [],
      };
      ___resultNamesByStep.forEach((result) => {
        if (result.step <= step) {
          const layer = this.resultsGroupLayer.findLayerById(result.result);
          if (layer) state.resultsGroupLayerItems.push(layer);
        }
      });
      return state;
    } //step 3

    if (step === 2 && this.result_dem && this.result_aoi && this.result_outlets && this.result_watershed && this.result_flowpath) {
      const watershedGraphics = this.cloneGraphics(
        this.result_watershed.features,
        validWatershedSymbol,
        this.mapView.spatialReference
      );
      const flowPathGraphics = this.cloneGraphics(
        this.result_flowpath.features,
        validFlowPathSymbol,
        this.mapView.spatialReference
      );
      const units: string = this.result_dem_boundary?.features[0].attributes['ElevationUnits'];
      const state: IUndoRedoState = {
        expressMode: false,
        in_dem_file: undefined,
        in_dem_elevation_units: units,
        in_generate_contours: this.result_contours ? true : false,
        in_contour_interval: 2, //this.in_contour_interval,
        in_generate_hillshade: this.result_hillshade ? true : false,
        in_generate_depthgrid: this.result_depthgrid ? true : false,
        in_generate_streamlines: this.in_generate_streamlines,
        in_stream_threshold: 2, //this.in_stream_threshold,

        // salLandArray: [],
        salSoilArray: [],
        rcnWatershedArray: [],
        rcnSubareasArray: [],

        aoiGraphics: [],
        outletGraphics: [],
        culvertGraphics: [],
        watershedGraphics: watershedGraphics,
        flowPathGraphics: flowPathGraphics,
        separatorGraphics: [],
        flowpathSegmentsGraphics: [],
        salLandGraphics: undefined,
        salSoilGraphics: [],
        rcnWatershedGraphics: [],

        result_dem: this.result_dem,
        result_dem_boundary: this.result_dem_boundary,
        result_aoi: this.result_aoi,
        result_culverts: this.result_culverts,
        result_clipdem: this.result_clipdem,
        result_hillshade: this.result_hillshade,
        result_contours: this.result_contours,
        result_depthgrid: this.result_depthgrid,
        result_streamlines: this.result_streamlines,
        result_outlets: undefined,
        result_watershed: this.result_watershed,
        result_flowpath: undefined,

        output_SAL_WATERSHED: undefined,
        result_soils: undefined,
        output_SAL_LANDUSE: undefined,
        output_RCN_WATERSHED: undefined,
        output_RCN_SUBAREAS: undefined,

        resultsGroupLayerItems: [],
      };
      ___resultNamesByStep.forEach((result) => {
        if (result.step <= step) {
          const layer = this.resultsGroupLayer.findLayerById(result.result);
          if (layer) state.resultsGroupLayerItems.push(layer);
        }
      });
      return state;
    } //step 2
    if (step === 1 && this.result_dem && this.result_aoi) {
      const clonedOutletsGraphicsArray = this.result_outlets
        ? this.cloneGraphics(this.result_outlets.features, outletGraphicSymbol, this.mapView.spatialReference)
        : [];
      const clonedCulvertsGraphicsArray = this.result_culverts
        ? this.cloneGraphics(this.result_culverts.features, culvertGraphicSymbol, this.mapView.spatialReference)
        : [];
      const units: string = this.result_dem_boundary?.features[0].attributes['ElevationUnits'];
      const state: IUndoRedoState = {
        expressMode: false,
        in_dem_file: undefined,
        in_dem_elevation_units: units,
        in_generate_contours: this.result_contours ? true : false,
        in_contour_interval: 2, //this.in_contour_interval,
        in_generate_hillshade: this.result_hillshade ? true : false,
        in_generate_depthgrid: this.result_depthgrid ? true : false,
        in_generate_streamlines: this.in_generate_streamlines,
        in_stream_threshold: 2, //this.in_stream_threshold,
        aoiGraphics: [],
        outletGraphics: clonedOutletsGraphicsArray,
        culvertGraphics: clonedCulvertsGraphicsArray,
        watershedGraphics: [],
        flowPathGraphics: [],
        separatorGraphics: [],
        flowpathSegmentsGraphics: [],
        salLandGraphics: undefined,
        salSoilGraphics: [],
        rcnWatershedGraphics: [],

        // salLandArray: [],
        salSoilArray: [],
        rcnWatershedArray: [],
        rcnSubareasArray: [],

        result_dem: this.result_dem,
        result_dem_boundary: this.result_dem_boundary,
        result_aoi: this.result_aoi,
        result_culverts: undefined,
        result_clipdem: undefined,
        result_hillshade: undefined,
        result_contours: undefined,
        result_depthgrid: undefined,
        result_streamlines: undefined,
        result_outlets: undefined,
        result_watershed: undefined,
        result_flowpath: undefined,

        output_SAL_WATERSHED: undefined,
        result_soils: undefined,
        output_SAL_LANDUSE: undefined,
        output_RCN_WATERSHED: undefined,
        output_RCN_SUBAREAS: undefined,

        resultsGroupLayerItems: [],
      };
      ___resultNamesByStep.forEach((result) => {
        if (result.step <= step) {
          const layer = this.resultsGroupLayer.findLayerById(result.result);
          if (layer) state.resultsGroupLayerItems.push(layer);
        }
      });
      return state;
    } ///step 0
    if (step === 0 && this.result_dem) {
      const clonedAOIGraphicsArray = this.result_aoi
        ? this.cloneGraphics(this.result_aoi.features, aoiGraphicSymbol, this.mapView.spatialReference)
        : [];
      const units: string = this.result_dem_boundary?.features[0].attributes['ElevationUnits'];
      const state: IUndoRedoState = {
        expressMode: false,
        in_dem_file: undefined,
        in_dem_elevation_units: units,
        in_generate_contours: false,
        in_contour_interval: 2,
        in_generate_hillshade: true,
        in_generate_depthgrid: false,
        in_generate_streamlines: this.in_generate_streamlines,
        in_stream_threshold: 2,

        // salLandArray: [],
        salSoilArray: [],
        rcnWatershedArray: [],
        rcnSubareasArray: [],

        aoiGraphics: clonedAOIGraphicsArray,
        culvertGraphics: [],
        outletGraphics: [],
        watershedGraphics: [],
        flowPathGraphics: [],
        separatorGraphics: [],
        flowpathSegmentsGraphics: [],
        salLandGraphics: undefined,
        salSoilGraphics: [],
        rcnWatershedGraphics: [],

        result_dem: this.result_dem,
        result_dem_boundary: this.result_dem_boundary,
        result_aoi: undefined,
        result_culverts: undefined,
        result_clipdem: undefined,
        result_hillshade: undefined,
        result_contours: undefined,
        result_depthgrid: undefined,
        result_streamlines: undefined,
        result_outlets: undefined,
        result_watershed: undefined,
        result_flowpath: undefined,

        output_SAL_WATERSHED: undefined,
        result_soils: undefined,
        output_SAL_LANDUSE: undefined,
        output_RCN_WATERSHED: undefined,
        output_RCN_SUBAREAS: undefined,

        resultsGroupLayerItems: [],
      };
      ___resultNamesByStep.forEach((result) => {
        if (result.step <= step) {
          const layer = this.resultsGroupLayer.findLayerById(result.result);
          if (layer) state.resultsGroupLayerItems.push(layer);
        }
      });
      return state;
    } ///step 0
    return undefined;
  }

  private exportState(): IUndoRedoState {
    if (this.toggleHillshadeButton === true) {
      this.in_generate_hillshade = true;
      this.toggleHillshadeButton = false;
    }
    const state: IUndoRedoState = {
      expressMode: this.expressMode,
      in_dem_file: this.in_dem_file,
      in_dem_elevation_units: this.in_dem_elevation_units,
      in_generate_contours: this.in_generate_contours,
      in_contour_interval: this.in_contour_interval,
      in_generate_hillshade: this.in_generate_hillshade,
      in_generate_depthgrid: this.in_generate_depthgrid,
      in_generate_streamlines: this.in_generate_streamlines,
      in_stream_threshold: this.in_stream_threshold,

      // salLandArray: this.salLandArray,
      salSoilArray: this.salSoilArray,
      rcnWatershedArray: this.rcnWatershedArray,
      rcnSubareasArray: this.rcnSubareasArray,

      aoiGraphics: this.aoiGraphicsLayer.graphics.toArray(),
      culvertGraphics: this.culvertGraphicsLayer.graphics.toArray(),
      outletGraphics: this.outletGraphicsLayer.graphics.toArray(),
      watershedGraphics: this.watershedGraphicsLayer.graphics.toArray(),
      flowPathGraphics: this.flowPathGraphicsLayer.graphics.toArray(),
      separatorGraphics: this.separatorGraphicsLayer.graphics.toArray(),
      flowpathSegmentsGraphics: this.flowpathSegmentsGraphicsLayer.graphics.toArray(),
      salLandGraphics: this.edit_SAL_LANDUSE_graphicsLayerView
        ? (this.edit_SAL_LANDUSE_graphicsLayerView.layer as GraphicsLayer).graphics.toArray()
        : [],
      salSoilGraphics: this.salSoilGraphicsLayer.graphics.toArray(),
      rcnWatershedGraphics: this.rcnWatershedGraphicsLayer.graphics.toArray(),

      result_dem: this.result_dem,
      result_dem_boundary: this.result_dem_boundary,
      result_aoi: this.result_aoi,
      result_culverts: this.result_culverts,
      result_clipdem: this.result_clipdem,
      result_hillshade: this.result_hillshade,
      result_contours: this.result_contours,
      result_depthgrid: this.result_depthgrid,
      result_streamlines: this.result_streamlines,
      result_outlets: this.result_outlets,
      result_watershed: this.result_watershed,
      result_flowpath: this.result_flowpath,

      output_SAL_WATERSHED: this.output_SAL_WATERSHED,
      result_soils: this.result_soils,
      output_SAL_LANDUSE: this.output_SAL_LANDUSE,
      output_RCN_WATERSHED: this.output_RCN_WATERSHED,
      output_RCN_SUBAREAS: this.output_RCN_SUBAREAS,

      resultsGroupLayerItems: this.resultsGroupLayer.layers.toArray(),
    };
    return state;
  }

  private importState(state: IUndoRedoState) {
    this.expressMode = state.expressMode;
    this.in_dem_file = state.in_dem_file;
    this.in_dem_elevation_units = state.in_dem_elevation_units;
    this.in_generate_contours = state.in_generate_contours;
    this.in_contour_interval = state.in_contour_interval;
    this.in_generate_hillshade = state.in_generate_hillshade;
    this.in_generate_depthgrid = state.in_generate_depthgrid;
    this.in_generate_streamlines = state.in_generate_streamlines;
    this.in_stream_threshold = state.in_stream_threshold;

    this.aoiGraphicsLayer.removeAll();
    this.aoiGraphicsLayer.addMany(state.aoiGraphics);
    this.culvertGraphicsLayer.removeAll();
    this.culvertGraphicsLayer.addMany(state.culvertGraphics);
    this.outletGraphicsLayer.removeAll();
    this.outletGraphicsLayer.addMany(state.outletGraphics);
    this.watershedGraphicsLayer.removeAll();
    this.watershedGraphicsLayer.addMany(state.watershedGraphics);
    this.flowPathGraphicsLayer.removeAll();
    this.flowPathGraphicsLayer.addMany(state.flowPathGraphics);
    this.separatorGraphicsLayer.removeAll();
    this.separatorGraphicsLayer.addMany(state.separatorGraphics)
    this.flowpathSegmentsGraphicsLayer.removeAll();
    this.flowpathSegmentsGraphicsLayer.addMany(state.flowpathSegmentsGraphics)
    if (this.edit_SAL_LANDUSE_graphicsLayer) {
      this.edit_SAL_LANDUSE_graphicsLayer.removeAll();
      if (state.salLandGraphics) {
        this.edit_SAL_LANDUSE_graphicsLayer.addMany(state.salLandGraphics!);
      }
    }

    this.salSoilGraphicsLayer.removeAll();
    this.salSoilGraphicsLayer.addMany(state.salSoilGraphics);
    this.rcnWatershedGraphicsLayer.removeAll();
    this.rcnWatershedGraphicsLayer.addMany(state.rcnWatershedGraphics);

    this.result_dem = state.result_dem;
    this.result_dem_boundary = state.result_dem_boundary;
    this.result_aoi = state.result_aoi;
    this.result_culverts = state.result_culverts;
    this.result_clipdem = state.result_clipdem;
    this.result_hillshade = state.result_hillshade;
    this.result_contours = state.result_contours;
    this.result_depthgrid = state.result_depthgrid;
    this.result_streamlines = state.result_streamlines;
    this.result_outlets = state.result_outlets;
    this.result_watershed = state.result_watershed;
    this.result_flowpath = state.result_flowpath;

    this.output_SAL_WATERSHED = state.output_SAL_WATERSHED;
    this.result_soils = state.result_soils;
    this.output_SAL_LANDUSE = state.output_SAL_LANDUSE;
    this.output_RCN_WATERSHED = state.output_RCN_WATERSHED;
    this.output_RCN_SUBAREAS = state.output_RCN_SUBAREAS;

    this.resultsGroupLayer.removeAll();
    this.resultsGroupLayer.addMany(state.resultsGroupLayerItems);
    // turn the computed watershed layer off if showing the modified one
    this.hideResultWatershedLayer();
    // ensure outlets layer is on top
    this.ensureOutletsLayerOnTop();

    if (this.edit_SAL_LANDUSE_graphicsLayer) {
      this.edit_SAL_LANDUSE_graphicsLayer.visible = !this.landUseTableComplete;
    }
    if (this.result_soils_layer) {
      this.result_soils_layer.visible = this.landUseTableComplete;
    }
  }

  getResultLayerById(id: string) {
    return this.resultsGroupLayer.findLayerById(id);
  }

  @action.bound async saveLandUse(): Promise<boolean> {
    let result = false;

    if (/* this.landUseTableComplete && */ this.edit_SAL_LANDUSE_graphicsLayer) {
      const dataObject = {
        lulc: this.edit_SAL_LANDUSE_graphicsLayer.graphics.toArray().map((g) => g.toJSON()),
        complete: this.landUseTableComplete,
      };
      const dataString = JSON.stringify(dataObject);
      const sessionUuid = efh2Store.projectsStore.currentSessionId;
      const postData = {
        id: 0,
        sessionUuid: sessionUuid,
        uiStateType: __lulcInputTypeKey,
        sessionUiStateValue: dataString,
        userId: localStorage.getItem('username') as string,
      };
      const response = await PRJAPI.EftProjUserInputController.createProjectInputUsingPOST([postData]);
      const responseData = response.data;

      await this.createActivityLog('LULC', 'Edit Land Use', 'Land use and condition defined', 'Create');

      if (responseData[0]) {
        result = true;
      } else {
        throw new Error('Failed to save Land Use/Land Cover layer as project inputs');
      }
    }
    return result;
  }

  @action.bound clearDemGraphics() {
    this.in_dem_file = undefined;
    this.result_dem = undefined;
    this.result_dem_boundary = undefined;
    this.resultsGroupLayer.removeAll();
  }

  // for post projection Text 

  @observable createProjectionText: ICreateProjectionText = {
    projectUuid: '',
    projectionText: '',
  };

  @action.bound async updateProjectionText() {
    this.createProjectionText.projectUuid = efh2Store.projectsStore.currentProject.projectUuid;
    this.createProjectionText.projectionText = this.projectionText
    efh2Store.projectsStore.currentProject.projectionText = this.projectionText
    const postJSON: EftProjectProjectionTextDto = {
      projectUuid: efh2Store.projectsStore.currentProject.projectUuid,
      projectionText: this.projectionText
    };
    try {
      const response = await PRJAPI.ProjectionTextController.updateProjectProjectionTextUsingPOST(postJSON);
      const data = (response.data as unknown) as projeectionTextObj;

    } catch (error) {
      console.error(error)
    }
  }
  // end for method of projection Text

  public static async loadSavedLandUse() {
    let result = undefined;
    const sessionUuid = efh2Store.projectsStore.currentSessionId;
    // const responseData = await projectUserInputAPI.getProjectUserInput(sessionUuid, lulcInputTypeKey);

    const response = await PRJAPI.EftProjUserInputController.getProjUserInputByUuuidAndTypeUsingGET(
      sessionUuid,
      __lulcInputTypeKey
    );
    const responseData = response.data;

    if (responseData && responseData[0]) {
      const data = JSON.parse(responseData[0].sessionUiStateValue);
      if (data && undefined !== data.lulc) {
        result = data;
      }
    }
    return result;
  }

  public async deleteSavedLandUse() {
    const sessionUuid = efh2Store.projectsStore.currentSessionId;
    const response = await PRJAPI.EftProjUserInputController.deleteProjUserInputByUuuidAndTypeUsingDELETE(
      sessionUuid,
      __lulcInputTypeKey
    );
  }

  private async getProjectCountyExtent(): Promise<void> {
    const project = stores.AppStore.efh2Store.projectsStore.currentProject;
    if (project && project.state !== '') {
      const response = await PRJAPI.StateCountyController.getCountiesByStateUsingGET(project.state);
      if (response.data) {
        const counties = response.data;

        if (project.county === '') {
          // compute state bounding coordinates
          let stateXMin: number | undefined = undefined;
          let stateXMax: number | undefined = undefined;
          let stateYMin: number | undefined = undefined;
          let stateYMax: number | undefined = undefined;
          for (const co of counties) {
            DelineateWatershedInputStateVM.normalizeCountyXRange(co);
            if ((co.xmin !== undefined) && (stateXMin === undefined || stateXMin > co.xmin)) {
              stateXMin = co.xmin;
            }
            if ((co.xmax !== undefined) && (stateXMax === undefined || stateXMax < co.xmax)) {
              stateXMax = co.xmax;
            }
            if ((co.ymin !== undefined) && (stateYMin === undefined || stateYMin > co.ymin)) {
              stateYMin = co.ymin;
            }
            if ((co.ymax !== undefined) && (stateYMax === undefined || stateYMax < co.ymax)) {
              stateYMax = co.ymax;
            }
          }
          if (
            stateXMin !== undefined &&
            stateXMax !== undefined &&
            stateYMin !== undefined &&
            stateYMax !== undefined
          ) {
            this.countyExtent = new Extent({
              xmin: stateXMin,
              xmax: stateXMax,
              ymin: stateYMin,
              ymax: stateYMax,
              spatialReference: new SpatialReference({ wkid: 4326 }),
            }) as Geometry;
          }
          return;
        }
        const county = counties.find((co) => {
          return co.county_name === project.county;
        });
        if (
          county &&
          county.xmin !== undefined &&
          county.xmax !== undefined &&
          county.ymin !== undefined &&
          county.ymax !== undefined
        ) {
          DelineateWatershedInputStateVM.normalizeCountyXRange(county);
          this.countyExtent = new Extent({
            xmin: county.xmin,
            xmax: county.xmax,
            ymin: county.ymin,
            ymax: county.ymax,
            spatialReference: new SpatialReference({ wkid: 4326 }),
          }) as Geometry;
        }
      }
    }
  }

  private static normalizeCountyXRange(county: StateCountyDto): void {
    if (
      county.xmin !== undefined &&
      county.xmax !== undefined &&
      county.xmax - county.xmin > 10000000 &&
      county.xmax > 0 &&
      county.xmin < 0
    ) {
      const minX = county.xmax + -20026376.39 * 2.0;
      county.xmax = county.xmin;
      county.xmin = minX;
    }
  }

  public async createActivityLog(
    activityContent: string,
    appAccessed: string,
    comment: string,
    crudCode: 'Create' | 'Update',
    projectStatus?: string,
    userName?: string,
  ) {
    let status = 'Started';
    if (projectStatus) {
      status = projectStatus;
    }
    const activityJson = {
      activityContent: activityContent,
      appAccessed: appAccessed,
      comment: comment,
      createdBy: UserIdentity.username,
      crudCode: crudCode,
      deleted: 0,
      lastChangedBy: userName ? userName : UserIdentity.username,
      projUuid: efh2Store.projectsStore.currentProjectId,
      projectStatus: status,
      sessionUuid: efh2Store.projectsStore.currentProject.sessionList[0].sessionUuid,
      userName: UserIdentity.username,
    };
    if (this.hasRedo || (this.currentStepIndex === 3 && this.inputLandUseSnapshot)) {
      activityJson.crudCode = 'Update';
    }
    await ActivityLogController.createActivityLogUsingPOST(activityJson);
  }

  private async saveProjectionText(): Promise<void> {
    if (this.result_dem_boundary && this.result_dem_boundary.features.length > 0) {
      const feature = this.result_dem_boundary.features[0];
      if (feature !== undefined) {
        try {
          let projectionText: string | undefined = feature.attributes['SpatialReference'];
          if (projectionText !== undefined) {
            projectionText = (projectionText as string).replaceAll('_', ' ');
            const projectUuid = stores.AppStore.efh2Store.projectsStore.currentProjectId as string;
            const dto: EftProjectProjectionTextDto = {
              projectUuid: projectUuid,
              projectionText: projectionText
            };
            await PRJAPI.EftProjectController.updateProjectProjectionTextUsingPOST(dto);
          }
        } catch (e) {
          console.warn(`Error saving projection description for the current project: ${(e as Error).message}`);
        }
      }
    }
  }

  private async takeAoiScreenshot(): Promise<void> {
    if (
      (this.result_watershed !== undefined && 0 < this.result_watershed.features.length) ||
      (this.result_aoi !== undefined && 0 < this.result_aoi.features.length) ||
      (this.aoiGraphicsLayer !== undefined && 0 < this.aoiGraphicsLayer.graphics.length)
    ) {
      let hasError = false;
      try {
        const aoi =
          this.result_watershed?.features[0] || this.result_aoi?.features[0] || this.aoiGraphicsLayer.graphics.at(0);
        let aoiGeometry: Geometry;
        if (aoi.geometry.spatialReference.equals(this.mapView.spatialReference)) {
          aoiGeometry = aoi.geometry;
        } else {
          await esriProjection.load();
          aoiGeometry = esriProjection.project(aoi.geometry, this.mapView.spatialReference) as Geometry;
        }
        const extent = aoiGeometry.extent.clone();
        extent.expand(1.1);
        const llPoint = new Point({
          x: extent.xmin,
          y: extent.ymin,
          spatialReference: this.mapView.spatialReference,
        });
        const urPoint = new Point({
          x: extent.xmax,
          y: extent.ymax,
          spatialReference: this.mapView.spatialReference,
        });
        const llScreenPoint = this.mapView.toScreen(llPoint);
        const urScreenPoint = this.mapView.toScreen(urPoint);
        let imageWidth = urScreenPoint.x - llScreenPoint.x;
        let imageHeight = llScreenPoint.y - urScreenPoint.y;
        const minDimension = ((imageWidth < imageHeight) ? imageWidth : imageHeight);
        if (minDimension < __minScreenshotSize) {
          const ratio = __minScreenshotSize / minDimension;
          imageWidth = imageWidth * ratio;
          imageHeight = imageHeight * ratio;
        }
        // console.warn('IMAGE WIDTH:', imageWidth);
        // console.warn('IMAGE HEIGHT:', imageHeight);
        const area = {
          x: llScreenPoint.x,
          y: urScreenPoint.y,
          width: urScreenPoint.x - llScreenPoint.x,
          height: llScreenPoint.y - urScreenPoint.y,
        };
        const screenshot = await this.mapView.takeScreenshot({
          width: imageWidth,
          height: imageHeight,
          area: area,
          format: 'png',
          ignorePadding: true
        });

        const projectUuid = stores.AppStore.efh2Store.projectsStore.currentProjectId as string;
        await gisUtils.saveScreenshot(projectUuid, screenshot.dataUrl);
      } catch (e) {
        hasError = true;
      } finally {
        if (hasError) {
          await MessageCenter.addNotificationFromCode('Common_20', [
            {
              callback: () => {
                MessageCenter.clearMessages();
              },
              text: 'OK',
            },
          ]);
        }
      }
    }
  }

  private async checkAoiTooLarge(): Promise<boolean> {
    let result = false;

    if (this.aoiGraphicsLayer && (this.aoiGraphicsLayer.graphics.length > 0)) {
      if (this.demFileCellSize === undefined) {
        if (this.result_dem_boundary && (this.result_dem_boundary.features.length > 0)) {
          const feature = this.result_dem_boundary.features[0];
          if (feature !== undefined) {
            this.demFileCellSize = feature.attributes['CellSize'];
            // console.warn('FILE CELL SIZE:', this.demFileCellSize);
          }
        }
      }
      if ((this.demFileCellSize !== undefined) && (this.workingSpatialReference !== undefined)) {
        let aoiGeometry = this.aoiGraphicsLayer.graphics.at(0).geometry;
        if (!aoiGeometry.spatialReference.equals(this.workingSpatialReference)) {
          await esriProjection.load();
          aoiGeometry = esriProjection.project(this.aoiGraphicsLayer.graphics.at(0).geometry, this.workingSpatialReference) as Geometry;
        }
        const aoiExtent = aoiGeometry.extent;
        const widthCells = Math.ceil((aoiExtent.xmax - aoiExtent.xmin) / this.demFileCellSize);
        const hightCells = Math.ceil((aoiExtent.ymax - aoiExtent.ymin) / this.demFileCellSize);
        // console.log('AOI SIZE:', widthCells, hightCells);
        // using an empirical coefficient of 1.3 to account for AOI buffering
        if (demClipMaxSize < widthCells * hightCells /* * 1.3 */) {
          result = true;
        }
      }
    }

    return result;
  }

  private static async getAoiBBoxInUtm(aoi: Polygon): Promise<IAoiBBoxInUtm | undefined> {
    let result: IAoiBBoxInUtm | undefined = undefined;
    if (aoi && aoi.spatialReference) {
      const utmWkid = await gisUtils.getUtmZoneWkid(aoi.centroid);
      if (((utmWkid > 32600) && (utmWkid < 32661)) || ((utmWkid > 32700) && (utmWkid < 32761))) {
        const utmCrs = new SpatialReference({ wkid: utmWkid });
        if (!esriProjection.isLoaded()) {
          await esriProjection.load();
        }
        const aoiUtm = esriProjection.project(aoi, utmCrs) as Polygon;
        if (aoiUtm) {
          const aoiExtent = aoiUtm.extent;
          const halfWidth = (aoiExtent.xmax - aoiExtent.xmin) / 2.0;
          const halfHeight = (aoiExtent.ymax - aoiExtent.ymin) / 2.0;
          result = {
            crs: utmCrs,
            centerX: aoiExtent.xmin + halfWidth,
            centerY: aoiExtent.ymin + halfHeight,
            halfWidth: halfWidth,
            halfHeight: halfHeight
          };
        }
      }
    }
    return result;
  }

  private static getAvailableDemService(demServiceProps: any, aoiUtm?: IAoiBBoxInUtm): IAvailableDemService {
    const result: IAvailableDemService = {
      id: demServiceProps.id as string,
      title: demServiceProps.title as string,
      notEligible: true
    };
    if (aoiUtm) {
      const cellHalfWidth = Math.ceil(aoiUtm.halfWidth / demServiceProps.cellSize);
      const cellHalfHeight = Math.ceil(aoiUtm.halfHeight / demServiceProps.cellSize);
      const width = cellHalfWidth * 2;
      const height = cellHalfHeight * 2;
      if (((width * height) <= demClipMaxSize) && (width <= demServiceProps.maxImageWidth) && (height <= demServiceProps.maxImageHeight)) {
        result.notEligible = false;
        result.url = demServiceProps.url as string;
        result.pixelType = demServiceProps.pixelType as string;
        result.cellSize = demServiceProps.cellSize as number;
        result.width = width;
        result.height = height;
        result.crs = aoiUtm.crs;

        const imgHalfWidth = cellHalfWidth * demServiceProps.cellSize;
        const imgHalfHeight = cellHalfHeight * demServiceProps.cellSize;
        result.bBoxXMin = aoiUtm.centerX - imgHalfWidth;
        result.bBoxYMin = aoiUtm.centerY - imgHalfHeight;
        result.bBoxXMax = aoiUtm.centerX + imgHalfWidth;
        result.bBoxYMax = aoiUtm.centerY + imgHalfHeight;
      }
    }
    return result;
  }

  private async getDemServiceProperties(demURL: string) {
    let result;
    await esriRequest(demURL + '?f=json', {
      responseType: 'json',
    })
      .then((response) => {
        if (response.data) {
          result = response.data;
        }
      })
      .catch((error) => {
        result = undefined;
      });
    return result;
  }

  private static getDemServiceCellSize(demServiceProps): number {
    const metersPerUnit = (demServiceProps.spatialReference.wkid === 4326 ? 111319.490793274 : 1);
    return (demServiceProps.pixelSizeX * metersPerUnit);
  }

  public setCellSizeStreamThreshold(cellSize: number, units: GpLinearUnit = 'meters'): void {
    const metersPerUnit = (units === 'meters' ? 1 : 3.2808333333);
    this.currentDemCellSize = cellSize / metersPerUnit;
    if ((this.currentDemCellSize! > 9) && (this.in_stream_threshold < 8)) {
      this.in_stream_threshold = 8;
    } else if ((this.currentDemCellSize! > 4) && (this.in_stream_threshold < 4)) {
      this.in_stream_threshold = 4;
    }
  }

  private async clipDemFromService(demServiceProps: IAvailableDemService) {
    let result: any = undefined;

    const clipQueryUrl = demServiceProps.url + '/exportImage';
    const bBox = `${demServiceProps.bBoxXMin},${demServiceProps.bBoxYMin},${demServiceProps.bBoxXMax},${demServiceProps.bBoxYMax}`;
    const imageSize = `${demServiceProps.width},${demServiceProps.height}`;

    const clipQueryParams = {
      bbox: bBox,
      bboxSR: demServiceProps.crs,
      size: imageSize,
      imageSR: demServiceProps.crs,
      format: 'tiff',
      pixelType: demServiceProps.pixelType,
      noData: -9999,
      noDataInterpretation: 'esriNoDataMatchAny',
      interpolation: 'RSP_BilinearInterpolation', // 'RSP_NearestNeighbor',
      compression: 'LZ77',
      bandIds: '0',
      f: 'json'
    };
    // console.warn('CLIP PARAMETERS');
    // console.warn(clipQueryParams);

    const maxTries = 10;
    let counter = 0;
    while (counter < maxTries) {
      await esriRequest(clipQueryUrl, {
        method: 'post',
        query: clipQueryParams,
        responseType: 'json',
        timeout: demServiceTimeout,
      })
        .then((response) => {
          if (response.data) {
            result = response.data;
          }
        })
        .catch((error) => {
          result = undefined;
        });
      if (result !== undefined) {
        break;
      }
      counter++;
    }
    return result;
  }

  private async identifyDemValue(demServiceUrl: string, point: Point) {
    let result;

    const maxTry = 10;
    const identifyUrl = demServiceUrl + '/identify';
    const identifyParams = {
      geometry: point,
      geometryType: 'esriGeometryPoint',
      returnGeometry: false,
      returnCatalogItems: false,
      returnPixelValues: false,
      f: 'json'
    }

    for (let i = 0; i < maxTry; i++) {
      await esriRequest(identifyUrl, {
        method: 'post',
        query: identifyParams,
        responseType: 'json',
        timeout: demIdentifyTimeout,
      }).then((response) => {
        if (response.data) {
          result = response.data;
        }
      }).catch((error) => {
        console.warn('DEM IMAGE IDENTIFY ERROR:', identifyUrl, error.message);
      });
      if (result) {
        break;
      }
    }
    return result;
  }

  private static computeBBoxCoord(
    aoiLimit: number,
    serviceLimit: number,
    pixelSize: number,
    roundUp: boolean | undefined = false
  ) {
    const pixelLimit = (aoiLimit - serviceLimit) / pixelSize;
    const pixelEdge = roundUp ? Math.ceil(pixelLimit) : Math.floor(pixelLimit);
    const edgeLimit = pixelEdge * pixelSize + serviceLimit;
    return { edgeCoord: edgeLimit, edgePixel: pixelEdge };
  }

  private async persistWatershedInformation(inFeature: Graphic) {

    const sessionUuid = efh2Store.projectsStore.currentProject.sessionList[0].sessionUuid;
    // console.log('SessionUuId:', sessionUuid);

    const watershedAcreage = inFeature?.attributes["Acres"].toFixed(4);
    if (inFeature) {
      this.setWatershedAcreage(inFeature.attributes["Acres"]);
    }

    const demName = this.in_dem_title;

    const centroid = (inFeature?.geometry as Polygon)?.centroid
    const spReference = new SpatialReference({ wkid: 4326 })

    await esriProjection.load();
    const ptCentroidDD = esriProjection.project(centroid, spReference) as Point; // reproject centroid

    const watershedLatLng = ptCentroidDD?.latitude.toFixed(4) + ", " + ptCentroidDD?.longitude.toFixed(4);

    // requestDto for latitude and longitude
    const watershedLatLngDto = {
      "key": "watershedLatitudeLongitude",
      "value": watershedLatLng?.toString()
    }

    // Persist watershed lat/long to BE
    await HydroWatershedController.addOrUpdateHydroWatershedDesignUsingPOST(sessionUuid, watershedLatLngDto);

    // requestDto for acreage
    const watershedAcresDto = {
      "key": "watershedAcreage",
      "value": watershedAcreage?.toString()
    }

    // Persist watershed Acreage to BE
    await HydroWatershedController.addOrUpdateHydroWatershedDesignUsingPOST(sessionUuid, watershedAcresDto);

    // await this.persistWatershedDemName(demName);
  }

  private async persistWatershedAcreage(in_acres: number) {

    const sessionUuid = efh2Store.projectsStore.currentProject.sessionList[0].sessionUuid;

    const watershedAcresDto = {
      "key": "watershedAcreage",
      "value": in_acres.toString()
    }

    // Persist watershed Acreage to BE
    await HydroWatershedController.addOrUpdateHydroWatershedDesignUsingPOST(sessionUuid, watershedAcresDto);
  }

  public async persistWatershedDemName(in_dem_name: string) {

    const sessionUuid = efh2Store.projectsStore.currentProject.sessionList[0].sessionUuid;

    const demNameDto = {
      "key": "demName",
      "value": in_dem_name?.toString()
    }
    await HydroWatershedController.addOrUpdateHydroWatershedDesignUsingPOST(sessionUuid, demNameDto);
  }

  setSelectedDemName(name: string) {
    this.demSelectName = name;
  }

  setWatershedAcreage(acres: number) {
    this.watershedAcreage = acres;
  }

  deactivateSketchTools(tool: Sketch | SketchViewModel) {
    if (tool.state === 'active') {
      tool.cancel();
    }
  }

  private async watchHandle11CreateCallback(event) {
    let correctTopology: boolean = false;
    if (event.state === 'active') {
      if (event.graphic !== null) {
        const area = esriGeometryEngine.geodesicArea(event.graphic.geometry as Polygon, "acres");
        this.aoiArea = area.toFixed(2);
      }
    }

    if (event.state === 'complete') {
      const currentAoi = event.graphic.geometry;
      const lyr = this.aoiSketch.layer as GraphicsLayer;

      correctTopology = this.isTopologicallyCorrect(currentAoi as Polygon, false);
      event.graphic.symbol = correctTopology ? aoiGraphicSymbol : invalidWatershedSymbol;
      efh2Store.hasValidAoiPolygon = correctTopology;

      if (lyr) {
        if (lyr.graphics.length > 1) {
          const previousAoi = lyr.graphics.getItemAt(0);
          lyr.remove(previousAoi);
        }
        if (this.in_dem_service_selection === 'service') {
          await this.checkDemServicesAvailability();
        }
      }
    }
  }
  private watchHandle13UpdateCallback(event) {
    this.aoiSketch.cancel();
    this.culvertSketch.cancel();
    this.watershedSketch.cancel();
    this.flowPathSketch.cancel();

    if (event.state === 'complete') {
      const lyr = this.outletSketch.layer as GraphicsLayer;
      if (lyr) {
        while (lyr.graphics.length > 1) {
          const previousOutlet = lyr.graphics.getItemAt(0);
          lyr.remove(previousOutlet);
        }
      }
      this.hydrologyValidationError = false;
    }
  }
  private watchHandle9CreateCallback(event) {
    this.aoiSketch.cancel();
    this.culvertSketch.cancel();
    this.watershedSketch.cancel();
    this.flowPathSketch.cancel();

    if (event.state === 'complete') {
      const currentOutlet = event.graphic;
      const lyr = this.outletSketch.layer as GraphicsLayer;
      if (lyr) {
        while (lyr.graphics.length > 1) {
          const previousOutlet = lyr.graphics.getItemAt(0);
          lyr.remove(previousOutlet);
        }
      }
      this.hydrologyValidationError = false;
    }
  }

  @action.bound onClickUndoFlowPath() {
    if (this.flowPathSketch.canUndo()) {
      this.flowPathSketch.undo();
    }
  }

  @action.bound onClickRedoFlowPath() {
    if (this.flowPathSketch.canRedo()) {
      this.flowPathSketch.redo();
    }
  }

  @action.bound onClickResetSmartCut() {
    if (this.originalWatershedGraphic) {
      const previousGraphics = this.originalWatershedGraphic.clone();
      this.watershedGraphicsLayer.graphics.removeAll();
      this.watershedGraphicsLayer.graphics.add(previousGraphics);
      this.canResetSmartCut = false;
      this.watershedModified = false;
    }
  }

  private flowPathSketchUndoHandler(event: __esri.SketchViewModelUndoEvent) {
    if (event.tool === 'reshape' && event.type === 'undo') {
      this.updateFlowPathProperties(event.graphics[0]);
    }
  }

  private flowPathSketchRedoHandler(event: __esri.SketchViewModelRedoEvent) {
    if (event.tool === 'reshape' && event.type === 'redo') {
      this.updateFlowPathProperties(event.graphics[0]);
    }
  }

  updateWatershedSymbol() {
    this.watershedGraphicsLayer.graphics.forEach((graphic) => {
      const contains = this.containsProjectedGeometry(this.result_aoi!.features[0].geometry, graphic.geometry);
      graphic.symbol = contains ? validWatershedSymbol : invalidWatershedSymbol;

      if (contains && efh2Store.aoiContainsWatershed === null) {
        efh2Store.aoiContainsWatershed = true;
      };
      if (!contains) {
        efh2Store.aoiContainsWatershed = false;
      };
    })
  }

  private updateFlowPathProperties(graphic: __esri.Graphic) {
    const length = esriGeometryEngine.geodesicLength(graphic.geometry as Polyline, "feet");
    this.flowPathLength = length.toFixed(2);

    const contains = this.containsProjectedGeometry(this.result_aoi!.features[0].geometry, graphic.geometry);
    graphic.symbol = contains ? validFlowPathSymbol : invalidFlowPathSymbol;

    if (contains) {
      efh2Store.aoiContainsFlowPath = true;
    } else {
      efh2Store.aoiContainsFlowPath = false;
    }

    this.canUndoFlowPath = this.flowPathSketch.canUndo() && this.isEditFlowPathOn;
    this.canRedoFlowPath = this.flowPathSketch.canRedo() && this.isEditFlowPathOn;

    this.hydrologyValidationError = false;
  }

  private flowPathSketchUpdateHandler_preventMove(e: __esri.SketchUpdateEvent) {
    if (e.state === 'start') {
      this.originalGeometry = e.graphics[0].geometry.clone();
    }

    // these conditions makes for nice preventMove behavior
    if (e.toolEventInfo && e.toolEventInfo.type === 'move-start') {
      this.preventMoveGeometry = e.graphics[0].geometry.clone();
    } else if (e.toolEventInfo && e.toolEventInfo.type === 'move') {
      e.graphics[0].geometry = this.preventMoveGeometry.clone();
    } else if (
      e.toolEventInfo &&
      (e.toolEventInfo.type === 'reshape-stop' || e.toolEventInfo.type === 'vertex-remove')
    ) {
      this.updateFlowPathProperties(e.graphics[0]);
    }

    if (e.state === 'complete') {
      this.canUndoFlowPath = false;
      this.canRedoFlowPath = false;
      const currentGeom = e.graphics[0].geometry;
      const originalGeom = this.originalGeometry;

      // compare if geometries are different before setting modified = true
      if (!esriGeometryEngine.equals(currentGeom, originalGeom)) {
        this.flowPathModified = true;
      }
      this.originalGeometry = undefined;
    }
  }

  @action.bound async onclickCreateFlowPath() {
    this.flowPathSketch.create('polyline');
    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  @action.bound async editWatershedSwitchHandler() {
    if (!this.isEditWatershedOn && this.velocityVm?.velocitySelected) {
      await MessageCenter.addNotificationFromCode('Hydro_113', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            return;
          },
          text: 'Cancel',
        },
        {
          callback: async () => {
            MessageCenter.clearMessages();
            this.velocityVm?.resetAction();
            this._toggleEditWatershed();
          },
          text: 'OK',
        },
      ]);
      return;
    };
    this._toggleEditWatershed();
  }

  @action.bound private _toggleEditWatershed() {
    this.isEditWatershedOn = !this.isEditWatershedOn;
    if (this.isEditWatershedOn) {
      this.watershedSketch.viewModel.updateOnGraphicClick = true;
    } else {
      this.watershedSketch.cancel();
      this.watershedSketch.viewModel.updateOnGraphicClick = false;
    }
  }

  @action.bound async editFlowPathSwitchHandler() {
    if (!this.isEditFlowPathOn && this.velocityVm?.velocitySelected) {
      await MessageCenter.addNotificationFromCode('Hydro_113', [
        {
          callback: () => {
            MessageCenter.clearMessages();
            return;
          },
          text: 'Cancel',
        },
        {
          callback: () => {
            MessageCenter.clearMessages();
            this.velocityVm?.resetAction();
            this._toggleEditFlowPath();
          },
          text: 'OK',
        },
      ]);
      return;
    };
    this._toggleEditFlowPath();
  }

  @action.bound private _toggleEditFlowPath() {
    this.isEditFlowPathOn = !this.isEditFlowPathOn;

    if (this.isEditFlowPathOn) {
      this.flowPathSketch.updateOnGraphicClick = true;
      this.createPolylineDisabled = false;
    } else {
      this.flowPathSketch.cancel();
      this.flowPathSketch.updateOnGraphicClick = false;
      this.createPolylineDisabled = true;
    }

    this.canUndoFlowPath = this.flowPathSketch.canUndo() && this.isEditFlowPathOn;
    this.canRedoFlowPath = this.flowPathSketch.canRedo() && this.isEditFlowPathOn;
  }

  private watchHandle23CreateCallback(event) {
    this.aoiSketch.cancel();
    this.outletSketch.cancel();
    this.culvertSketch.cancel();
    this.watershedSketch.cancel();

    if (event.state === 'active' || event.state === 'complete') {
      this.updateFlowPathProperties(event.graphic);
    }

    if (event.state === 'complete') {
      const lyr = this.flowPathSketch.layer as GraphicsLayer;
      if (lyr) {
        while (lyr.graphics.length > 1) {
          lyr.graphics.getItemAt(1).attributes = lyr.graphics.getItemAt(0).attributes;
          lyr.graphics.getItemAt(1).attributes['Length_ft'] = esriGeometryEngine.geodesicLength(lyr.graphics.getItemAt(1).geometry, "feet");
          lyr.graphics.getItemAt(1).attributes['Shape_Length'] = esriGeometryEngine.geodesicLength(lyr.graphics.getItemAt(1).geometry, "meters");
          const previousFlowPath = lyr.graphics.getItemAt(0);
          lyr.remove(previousFlowPath);
          this.flowPathModified = true;
        }
      }
      this.canUndoFlowPath = false;
      this.canRedoFlowPath = false;
    }
  }
  async callConvertGDB() {
    const waitOptions: __esri.GeoprocessorWaitForJobCompletionOptions = {
      interval: 1000,
      signal: undefined,
      statusCallback: this.statusCallback,
    };
    return await this.api.convertGdbToGeotiff(
      this.gpSessionId,
      efh2Store.projectsStore.currentProject.projectUuid.toUpperCase(),
      waitOptions
    );
  }
}

interface IInputState {
  expressMode: boolean;
  in_dem_title?: string;
  in_dem_service_selection: 'service' | 'upload' | undefined;
  in_dem_file_name?: string; // this variable helps snapshot change detection for the file
  in_dem_elevation_units: string;
  in_generate_contours: boolean;
  in_contour_interval: number;
  in_generate_hillshade: boolean;
  in_generate_depthgrid: boolean;
  in_generate_streamlines: boolean;
  in_stream_threshold: number;

  // salLandArray: any[];
  salSoilArray: any[];

  aoiGraphics: Graphic[];
  culvertGraphics: Graphic[];
  outletGraphics: Graphic[];
  watershedGraphics: Graphic[];
  flowPathGraphics: Graphic[];
  separatorGraphics: Graphic[];
  flowpathSegmentsGraphics: Graphic[];
}

interface IUndoRedoState {
  //declare tracked undo/redo properites

  expressMode: boolean;
  in_dem_file?: File;
  in_dem_elevation_units: string;
  in_generate_contours: boolean;
  in_contour_interval: number;
  in_generate_hillshade: boolean;
  in_generate_depthgrid: boolean;
  in_generate_streamlines: boolean;
  in_stream_threshold: number;

  // salLandArray: any[];
  salSoilArray: any[];
  rcnWatershedArray: any[];
  rcnSubareasArray: any[];

  aoiGraphics: Graphic[];
  culvertGraphics: Graphic[];
  outletGraphics: Graphic[];
  watershedGraphics: Graphic[];
  flowPathGraphics: Graphic[];
  separatorGraphics: Graphic[];
  flowpathSegmentsGraphics: Graphic[];
  salLandGraphics?: Graphic[];
  salSoilGraphics: Graphic[];
  rcnWatershedGraphics: Graphic[];

  result_dem?: RasterData;
  result_dem_boundary?: FeatureSet;
  result_aoi?: FeatureSet;
  result_culverts?: FeatureSet;
  result_clipdem?: RasterData;
  result_hillshade?: RasterData;
  result_contours?: FeatureSet;
  result_depthgrid?: RasterData;
  result_streamlines?: FeatureSet;
  result_outlets?: FeatureSet;
  result_watershed?: FeatureSet;
  result_flowpath?: FeatureSet;

  output_SAL_WATERSHED?: FeatureSet;
  result_soils?: FeatureSet;
  output_SAL_LANDUSE?: FeatureSet;
  output_RCN_WATERSHED?: FeatureSet;
  output_RCN_SUBAREAS?: FeatureSet;

  resultsGroupLayerItems: Layer[];
}
// for projection Text
interface ICreateProjectionText {
  projectUuid: string,
  projectionText: string,
}

interface IAoiBBoxInUtm {
  crs: SpatialReference;
  centerX: number;
  centerY: number;
  halfWidth: number;
  halfHeight: number;
}

interface IAvailableDemService {
  id: string;
  title: string;
  notEligible: boolean;
  url?: string,
  pixelType?: string,
  cellSize?: number,
  width?: number,
  height?: number,
  bBoxXMin?: number,
  bBoxYMin?: number,
  bBoxXMax?: number,
  bBoxYMax?: number,
  crs?: SpatialReference
}